import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  checkProviderConnectionHealth,
  completeFanvueOAuth,
  completeTelegramAccountLogin,
  connectModelFanltvOfapiAccount,
  connectModelFanslyAccount,
  connectModelOnlyfansApiAccount,
  connectModelOnlyMonsterAccount,
  createTelegramBotPaymentInvoiceLink,
  getAigirlfactorySettings,
  getFanvuePlatformOAuthSettings,
  getFanvueOAuthSettings,
  getFanslyAuthenticationStatus,
  getFanslyDirectSettings,
  getInstagramOAuthSettings,
  getApiErrorMessage,
  getModels,
  getTelegramAccountAppSettings,
  getTelegramBotPaymentsSettings,
  getTelegramTributeSettings,
  listFanslyAuthAccounts,
  reauthenticateFanslyAccount,
  startFanvuePlatformOAuth,
  startFanvueOAuth,
  startInstagramOAuth,
  startFanslyAuthentication,
  startTelegramAccountLogin,
  syncFanslyMessages,
  syncFanvueMessages,
  submitFanslyAuthenticationCode,
  updateAigirlfactorySettings,
  updateFanvueOAuthSettings,
  updateFanslyDirectSettings,
  updateInstagramOAuthSettings,
  updateTelegramAccountAppSettings,
  updateTelegramBotPaymentsSettings,
  updateTelegramTributeSettings,
} from '../../api'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { asList, formatDate, modelTitle, providerLabel, providerLinks, providerStatus, providerWebhookDetail, providerWebhookStatus, statusTone } from './v2Utils'
import { rememberFanvueOAuthSession } from './fanvueOAuthState'
import FanvueFinancialHistory from '../../components/FanvueFinancialHistory'

const providerCatalog = [
  {
    key: 'fanvue',
    name: 'Fanvue',
    state: 'active',
    description: 'OAuth connection for creator chats, vault media and sales events.',
    actionLabel: 'Start OAuth',
    instructions: [
      'Pick the model that owns this creator account.',
      'Start OAuth and approve access in Fanvue.',
      'Return to this screen after the callback finishes.',
    ],
  },
  {
    key: 'fanvue_platform',
    name: 'Fanvue App',
    state: 'active',
    description: 'Connect creator accounts through the shared platform OAuth app.',
    actionLabel: 'Connect with app',
    instructions: [
      'Create the OAuth app in the Fanvue Developer Area once.',
      'Set the app redirect URI to this admin domain.',
      'Pick the model and approve access from the creator account.',
    ],
  },
  {
    key: 'telegram_account',
    name: 'Telegram Account',
    state: 'active',
    description: 'MTProto user account connection so automation can reply as the model account.',
    actionLabel: 'Login account',
    instructions: [
      'Enter the phone number for the model Telegram account.',
      'Submit the login code sent by Telegram.',
      'Enter the two-step password only when Telegram asks for it.',
    ],
  },
  {
    key: 'telegram_tribute',
    name: 'Telegram Tribute',
    state: 'active',
    description: 'Tribute private-chat and payment webhook channel for Telegram creators.',
    actionLabel: 'Configure webhook',
    instructions: [
      'Create or copy the API key in the Tribute creator dashboard.',
      'Save the key here to generate the model webhook URL.',
      'Paste the webhook URL into Tribute API Keys webhook settings.',
    ],
  },
  {
    key: 'telegram_bot_payments',
    name: 'Telegram Bot Payments',
    state: 'active',
    description: 'Native Telegram Stars checkout for digital content and paid media.',
    actionLabel: 'Configure Stars',
    instructions: [
      'Create a payment bot in BotFather and paste its bot token.',
      'Save and set the webhook so Telegram can confirm Stars payments.',
      'Generate invoice links and send them from the model Telegram account.',
    ],
  },
  {
    key: 'aigirlfactory',
    name: 'AIGirlFactory',
    state: 'active',
    description: 'Website and widget chat channel that routes visitors into the FanLTV graph.',
    actionLabel: 'Configure channel',
    instructions: [
      'Pick the FanLTV model that should answer this AIGirlFactory persona.',
      'Set persona key and channel to match the AIGF admin settings.',
      'Copy the endpoint URL and generated token into AIGirlFactory.',
    ],
  },
  {
    key: 'instagram',
    name: 'Instagram',
    state: 'active',
    description: 'Meta Instagram OAuth for creator profile access and future DM automation.',
    actionLabel: 'Start OAuth',
    instructions: [
      'Create an Instagram API app in Meta for Developers.',
      'Paste the app ID, app secret and scopes here, then save settings.',
      'Use the OAuth callback URL as the Valid OAuth Redirect URI in Meta.',
      'Use the webhook callback URL and verify token in Instagram Webhooks, then subscribe to messages.',
    ],
  },
  {
    key: 'onlyfansapi',
    name: 'OnlyFans',
    state: 'active',
    description: 'Vault media, chats, purchases and account sync.',
    actionLabel: 'Connect account',
    instructions: [
      'Choose the model for this OnlyFansAPI account.',
      'Paste the account id from OnlyFansAPI.',
      'Add a per-account API key only when the global key should not be used.',
    ],
  },
  {
    key: 'onlymonster',
    name: 'OnlyMonster',
    state: 'active',
    description: 'OnlyMonster public API for connected OnlyFans chats, vault metadata and thumbnails.',
    actionLabel: 'Connect account',
    instructions: [
      'Choose the model for this OnlyMonster account.',
      'Paste the OnlyMonster account id and API token.',
      'Media sync imports vault metadata and thumbnails; full files are not exposed by the public API.',
    ],
  },
  {
    key: 'fanltv_ofapi',
    name: 'FanLTV OFAPI Backup',
    state: 'active',
    description: 'Internal OnlyFansAPI-compatible backup for text sends when an external OnlyFans connector is already connected.',
    actionLabel: 'Connect backup',
    instructions: [
      'Connect OnlyFansAPI or OnlyMonster for this model first.',
      'Use this only as a backup because browser sessions can expire and may require manual captcha.',
      'Backup sending is limited to plain text; paid media stays on the external connector.',
    ],
  },
  {
    key: 'fansly',
    name: 'Fansly',
    state: 'active',
    description: 'Fansly messaging, media, PPV and account sync through OnlyFansAPI.',
    actionLabel: 'Connect account',
    instructions: [
      'Choose the FanLTV model that owns this Fansly account.',
      'Paste the Fansly API account id from OnlyFansAPI, or use username/Fansly id for lookup.',
      'Add a per-account API key only when the global OnlyFansAPI key should not be used.',
    ],
  },
  {
    key: 'custom_api',
    name: 'Custom API',
    state: 'planned',
    description: 'External CRM, website or private content source.',
    actionLabel: 'Planned',
    instructions: ['Custom connector setup will be added after the production provider contract is finalized.'],
  },
]

const fanvueStoreProvider = {
  key: 'fanvue_store',
  name: 'Fanvue',
  state: 'active',
  description: 'Secure Fanvue App Store connection for chats, fans and selected media.',
  actionLabel: 'Fanvue app connection',
  instructions: [
    'Your creator account is connected through Fanvue OAuth.',
    'Review the model setup before enabling automation.',
    'Reconnect only if Fanvue access was revoked or expired.',
  ],
}

function pickDefaultModelId(models) {
  const first = asList(models)[0]
  return first?.id ? String(first.id) : ''
}

function formatSyncStats(link) {
  const stats = link?.sync_stats || {}
  const created = Number(stats.messages_created || 0)
  const updated = Number(stats.messages_updated || 0)
  const chats = Number(stats.chats_requested || 0)
  const total = created + updated
  const date = formatDate(link?.sync_updated_at)
  return `${total} msgs, ${chats} chats - ${date}`
}

function linkMatchesProvider(link, providerKey) {
  const raw = String(link?.provider || '').toLowerCase()
  if (providerKey === 'fanvue_store') return raw === 'fanvue'
  if (providerKey === 'fanvue_platform') return raw === 'fanvue'
  if (providerKey === 'telegram_account') return raw === 'telegram_account' || raw === 'telegram'
  if (providerKey === 'telegram_tribute') return raw === 'telegram_tribute' || raw === 'tribute'
  if (providerKey === 'telegram_bot_payments') return raw === 'telegram_bot_payments' || raw === 'telegram_payments'
  if (providerKey === 'onlyfansapi') return raw === 'onlyfansapi' || raw === 'onlyfans'
  if (providerKey === 'onlymonster') return raw === 'onlymonster'
  if (providerKey === 'fansly') return raw === 'fansly'
  if (providerKey === 'fanltv_ofapi') return raw === 'fanltv_ofapi'
  return raw === providerKey
}

function defaultFanvueRedirectUri() {
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}/v2/connections/fanvue/callback`
}

function defaultInstagramRedirectUri() {
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}/api/providers/instagram/oauth-callback`
}

function telegramPhoneFromLink(link) {
  const candidates = [link?.external_id, link?.username, link?.email]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
  return candidates.find((value) => /^\+?\d[\d\s().-]{5,}$/.test(value)) || ''
}

function telegramReauthRequired(link) {
  const provider = String(link?.provider || '').toLowerCase()
  const status = providerStatus(link)
  const error = String(link?.connection_error || '').toLowerCase()
  return (provider === 'telegram' || provider === 'telegram_account')
    && status === 'failed'
    && error.includes('re-authentication required')
}

function connectionStatusLabel(link, fallbackStatus) {
  return telegramReauthRequired(link) ? 'Re-auth required' : fallbackStatus
}

export default function AdminV2Connections() {
  const { fanvueReviewWorkspace: fanvueOnlyWorkspace } = useWorkspaceScope()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [models, setModels] = useState([])
  const [selectedModelId, setSelectedModelId] = useState('')
  const [onlyfansapi, setOnlyfansapi] = useState({ busy: false, apiKey: '', accountId: '', connectionId: '', email: '', message: '', error: '' })
  const [onlymonster, setOnlymonster] = useState({ busy: false, apiToken: '', accountId: '', platformAccountId: '', username: '', email: '', message: '', error: '' })
  const [fansly, setFansly] = useState({
    busy: false,
    authBusy: false,
    authPolling: false,
    apiKey: '',
    accountId: '',
    fanslyId: '',
    username: '',
    password: '',
    proxyCountry: '',
    code: '',
    email: '',
    accounts: [],
    authStatus: null,
    message: '',
    error: '',
  })
  const [fanltvOfapi, setFanltvOfapi] = useState({ busy: false, apiKey: '', accountId: '', connectionId: '', email: '', message: '', error: '' })
  const [fanvuePlatform, setFanvuePlatform] = useState({ busy: false, settingsBusy: false, authUrl: '', state: '', message: '', error: '', settings: null })
  const [fanvue, setFanvue] = useState({ busy: false, code: '', state: '', creatorUserUuid: '', authUrl: '', message: '', error: '' })
  const [fanvueSettings, setFanvueSettings] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    clientId: '',
    clientSecret: '',
    clientSecretSet: false,
    metaAppId: '',
    metaAppSecret: '',
    metaAppSecretSet: false,
    redirectUri: '',
    authBaseUrl: '',
    apiBaseUrl: '',
    apiVersion: '',
    oauthScopes: '',
    webhookSecret: '',
    webhookSecretSet: false,
    webhookUrl: '',
    configured: false,
    source: '',
    message: '',
    error: '',
  })
  const [instagram, setInstagram] = useState({ busy: false, authUrl: '', state: '', message: '', error: '' })
  const [instagramSettings, setInstagramSettings] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    clientId: '',
    clientSecret: '',
    clientSecretSet: false,
    redirectUri: '',
    callbackUrl: '',
    webhookUrl: '',
    webhookVerifyToken: '',
    authBaseUrl: '',
    tokenUrl: '',
    graphBaseUrl: '',
    oauthScopes: '',
    configured: false,
    source: '',
    message: '',
    error: '',
  })
  const [fanvueSync, setFanvueSync] = useState({ busy: false, chatUuids: '', sinceMessageUuid: '', limit: 20, maxChats: 20, enqueueIncoming: false, message: '', error: '' })
  const [fanslySync, setFanslySync] = useState({ busy: false, limit: 25, maxChats: 25, enqueueUnanswered: false, message: '', error: '' })
  const [fanslyDirect, setFanslyDirect] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    sessionToken: '',
    sessionTokenSet: false,
    configured: false,
    enabled: false,
    verified: false,
    accountId: '',
    username: '',
    displayName: '',
    lastVerifiedAt: '',
    message: '',
    error: '',
  })
  const [telegram, setTelegram] = useState({ busy: false, phone: '', code: '', password: '', codeSent: false, passwordRequired: false, message: '', error: '' })
  const [telegramSettings, setTelegramSettings] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    apiId: '',
    apiHash: '',
    apiHashSet: false,
    configured: false,
    source: '',
    message: '',
    error: '',
  })
  const [tributeSettings, setTributeSettings] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    apiKey: '',
    apiKeySet: false,
    externalId: '',
    webhookUrl: '',
    configured: false,
    connected: false,
    productsProbeCount: null,
    lastHealthCheckAt: '',
    message: '',
    error: '',
  })
  const [telegramPayments, setTelegramPayments] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    botToken: '',
    botTokenSet: false,
    botUsername: '',
    botId: '',
    businessConnectionId: '',
    webhookUrl: '',
    webhookSecretSet: false,
    configured: false,
    connected: false,
    lastHealthCheckAt: '',
    invoiceTitle: 'Private content',
    invoiceDescription: 'Unlock this private content in Telegram.',
    invoiceStars: 50,
    invoicePayload: '',
    invoicePhotoUrl: '',
    invoiceLink: '',
    message: '',
    error: '',
  })
  const [aigfSettings, setAigfSettings] = useState({
    busy: false,
    saving: false,
    loadedModelId: '',
    personaKey: '',
    channel: '',
    apiToken: '',
    apiTokenSet: false,
    apiTokenOnce: '',
    endpointUrl: '',
    requireSignature: true,
    configured: false,
    connected: false,
    message: '',
    error: '',
  })
  const [healthCheck, setHealthCheck] = useState({ busyKey: '', message: '', error: '' })
  const [activeProvider, setActiveProvider] = useState('fanvue')
  const connectionPanelRef = useRef(null)
  const fanslyAuthCheckRef = useRef(false)
  useEffect(() => {
    let alive = true
    getModels()
      .then((result) => {
        if (!alive) return
        const list = asList(result)
        setModels(list)
        setSelectedModelId((current) => current || pickDefaultModelId(list))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load connections')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    let alive = true
    setFanvuePlatform((current) => ({ ...current, settingsBusy: true, error: '' }))
    getFanvuePlatformOAuthSettings()
      .then((result) => {
        if (!alive) return
        setFanvuePlatform((current) => ({ ...current, settingsBusy: false, settings: result || {}, error: '' }))
      })
      .catch((err) => {
        if (!alive) return
        setFanvuePlatform((current) => ({
          ...current,
          settingsBusy: false,
          settings: null,
          error: getApiErrorMessage(err, 'Failed to load Fanvue platform app settings'),
        }))
      })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!selectedModelId) return
    let alive = true
    setFanvueSettings((current) => ({ ...current, busy: true, error: '', message: '' }))
    getFanvueOAuthSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setFanvueSettings((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          clientId: result?.client_id || '',
          clientSecret: '',
          clientSecretSet: Boolean(result?.client_secret_set),
          redirectUri: result?.redirect_uri || defaultFanvueRedirectUri(),
          authBaseUrl: result?.auth_base_url || '',
          apiBaseUrl: result?.api_base_url || '',
          apiVersion: result?.api_version || '',
          oauthScopes: result?.oauth_scopes || '',
          webhookSecret: '',
          webhookSecretSet: Boolean(result?.webhook_secret_set),
          webhookUrl: result?.webhook_url || '',
          configured: Boolean(result?.configured),
          source: result?.source || '',
          error: '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setFanvueSettings((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            redirectUri: current.redirectUri || defaultFanvueRedirectUri(),
            error: getApiErrorMessage(err, 'Failed to load Fanvue OAuth settings'),
          }))
        }
      })
    return () => { alive = false }
  }, [selectedModelId])

  useEffect(() => {
    if (!selectedModelId || fanvueOnlyWorkspace) return
    let alive = true
    setInstagramSettings((current) => ({ ...current, busy: true, error: '', message: '' }))
    getInstagramOAuthSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        const fallbackCallback = defaultInstagramRedirectUri()
        setInstagramSettings((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          clientId: result?.client_id || '',
          clientSecret: '',
          clientSecretSet: Boolean(result?.client_secret_set),
          metaAppId: result?.meta_app_id || '',
          metaAppSecret: '',
          metaAppSecretSet: Boolean(result?.meta_app_secret_set),
          redirectUri: result?.redirect_uri || result?.callback_url || fallbackCallback,
          callbackUrl: result?.callback_url || fallbackCallback,
          webhookUrl: result?.webhook_url || '',
          webhookVerifyToken: result?.webhook_verify_token || '',
          authBaseUrl: result?.auth_base_url || 'https://www.instagram.com/oauth',
          tokenUrl: result?.token_url || 'https://api.instagram.com/oauth/access_token',
          graphBaseUrl: result?.graph_base_url || 'https://graph.instagram.com',
          oauthScopes: result?.oauth_scopes || 'instagram_business_basic,instagram_business_manage_messages',
          configured: Boolean(result?.configured),
          source: result?.source || '',
          error: '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setInstagramSettings((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            redirectUri: current.redirectUri || defaultInstagramRedirectUri(),
            callbackUrl: current.callbackUrl || defaultInstagramRedirectUri(),
            error: getApiErrorMessage(err, 'Failed to load Instagram OAuth settings'),
          }))
        }
      })
    return () => { alive = false }
  }, [selectedModelId, fanvueOnlyWorkspace])

  useEffect(() => {
    if (!selectedModelId || fanvueOnlyWorkspace) return
    let alive = true
    setTributeSettings((current) => ({ ...current, busy: true, error: '', message: '', apiKey: '' }))
    getTelegramTributeSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setTributeSettings((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          apiKey: '',
          apiKeySet: Boolean(result?.api_key_set),
          externalId: result?.external_id || '',
          webhookUrl: result?.webhook_url || '',
          configured: Boolean(result?.configured),
          connected: Boolean(result?.connected),
          productsProbeCount: result?.products_probe_count ?? null,
          lastHealthCheckAt: result?.last_health_check_at || '',
          error: result?.health_error || '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setTributeSettings((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            configured: false,
            connected: false,
            error: getApiErrorMessage(err, 'Failed to load Telegram Tribute settings'),
          }))
        }
      })
    return () => { alive = false }
  }, [selectedModelId, fanvueOnlyWorkspace])

  useEffect(() => {
    if (!selectedModelId || fanvueOnlyWorkspace) return
    let alive = true
    setTelegramPayments((current) => ({ ...current, busy: true, error: '', message: '', botToken: '', invoiceLink: '' }))
    getTelegramBotPaymentsSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setTelegramPayments((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          botToken: '',
          botTokenSet: Boolean(result?.bot_token_set),
          botUsername: result?.bot_username || '',
          botId: result?.bot_id || '',
          businessConnectionId: result?.business_connection_id || '',
          webhookUrl: result?.webhook_url || '',
          webhookSecretSet: Boolean(result?.webhook_secret_set),
          configured: Boolean(result?.configured),
          connected: Boolean(result?.connected),
          lastHealthCheckAt: result?.last_health_check_at || '',
          error: result?.health_error || '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setTelegramPayments((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            configured: false,
            connected: false,
            error: getApiErrorMessage(err, 'Failed to load Telegram Bot Payments settings'),
          }))
        }
      })
    return () => { alive = false }
  }, [selectedModelId, fanvueOnlyWorkspace])

  useEffect(() => {
    if (!selectedModelId || fanvueOnlyWorkspace) return
    let alive = true
    setTelegramSettings((current) => ({ ...current, busy: true, error: '', message: '' }))
    getTelegramAccountAppSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setTelegramSettings((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          apiId: result?.api_id || '',
          apiHash: '',
          apiHashSet: Boolean(result?.api_hash_set),
          configured: Boolean(result?.configured),
          source: result?.source || '',
          error: '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setTelegramSettings((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            configured: false,
            error: getApiErrorMessage(err, 'Failed to load Telegram app settings'),
          }))
        }
    })
    return () => { alive = false }
  }, [selectedModelId, fanvueOnlyWorkspace])

  useEffect(() => {
    if (!selectedModelId || fanvueOnlyWorkspace) return
    let alive = true
    setAigfSettings((current) => ({ ...current, busy: true, error: '', message: '', apiTokenOnce: '' }))
    getAigirlfactorySettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setAigfSettings((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          personaKey: result?.persona_key || '',
          channel: result?.channel || '',
          apiToken: '',
          apiTokenSet: Boolean(result?.api_token_set),
          apiTokenOnce: result?.api_token_once || '',
          endpointUrl: result?.endpoint_url || result?.endpoint_path || '/internal/channels/aigirlfriend/messages',
          requireSignature: result?.require_signature !== false,
          configured: Boolean(result?.configured),
          connected: Boolean(result?.connected),
          error: '',
        }))
      })
      .catch((err) => {
        if (alive) {
          setAigfSettings((current) => ({
            ...current,
            busy: false,
            loadedModelId: String(selectedModelId),
            configured: false,
            connected: false,
            error: getApiErrorMessage(err, 'Failed to load AIGirlFactory settings'),
          }))
        }
      })
    return () => { alive = false }
  }, [selectedModelId, fanvueOnlyWorkspace])

  const rows = useMemo(() => models
    .flatMap((model) => providerLinks(model).map((link) => ({ model, link })))
    .filter(({ link }) => !fanvueOnlyWorkspace || String(link?.provider || '').toLowerCase() === 'fanvue'),
  [models, fanvueOnlyWorkspace])
  const visibleProviderCatalog = fanvueOnlyWorkspace ? [fanvueStoreProvider] : providerCatalog
  const connected = rows.filter(({ link }) => providerStatus(link) === 'connected')
  const failed = rows.filter(({ link }) => ['failed', 'error', 'blocked'].includes(providerStatus(link)))
  const activeProviders = visibleProviderCatalog.filter((provider) => provider.state === 'active')

  const selectedModel = models.find((model) => String(model.id) === String(selectedModelId))
  const selectedProvider = visibleProviderCatalog.find((provider) => provider.key === activeProvider) || visibleProviderCatalog[0]
  const selectedProviderLink = selectedModel
    ? providerLinks(selectedModel).find((link) => linkMatchesProvider(link, activeProvider))
    : null
  const selectedProviderStatus = selectedProvider.state === 'planned'
    ? 'planned'
    : selectedProviderLink
      ? providerStatus(selectedProviderLink)
      : 'not connected'
  const selectedProviderStatusLabel = connectionStatusLabel(selectedProviderLink, selectedProviderStatus)
  const fanvuePlatformSettings = fanvuePlatform.settings || {}
  const fanvuePlatformConfigured = Boolean(fanvuePlatformSettings.configured)

  useEffect(() => {
    if (fanvueOnlyWorkspace && activeProvider !== 'fanvue_store') {
      setActiveProvider('fanvue_store')
    }
  }, [fanvueOnlyWorkspace, activeProvider])

  useEffect(() => {
    if (activeProvider !== 'fansly' || !selectedModelId) return undefined
    let alive = true
    setFanslyDirect((current) => ({ ...current, busy: true, error: '', message: '', sessionToken: '' }))
    getFanslyDirectSettings(selectedModelId)
      .then((result) => {
        if (!alive) return
        setFanslyDirect((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          sessionToken: '',
          sessionTokenSet: Boolean(result?.session_token_set),
          configured: Boolean(result?.configured),
          enabled: Boolean(result?.enabled),
          verified: Boolean(result?.verified),
          accountId: result?.account_id || '',
          username: result?.username || '',
          displayName: result?.display_name || '',
          lastVerifiedAt: result?.last_verified_at || '',
          error: result?.health_error || '',
        }))
      })
      .catch((err) => {
        if (!alive) return
        setFanslyDirect((current) => ({
          ...current,
          busy: false,
          loadedModelId: String(selectedModelId),
          configured: false,
          enabled: false,
          verified: false,
          error: getApiErrorMessage(err, 'Connect Fansly before configuring direct upload'),
        }))
      })
    return () => { alive = false }
  }, [activeProvider, selectedModelId, selectedProviderLink?.id])

  useEffect(() => {
    if (activeProvider !== 'telegram_account' || !selectedModelId) return
    const savedPhone = telegramPhoneFromLink(selectedProviderLink)
    setTelegram((current) => {
      const loginInProgress = current.codeSent || current.code || current.password || current.passwordRequired
      if (savedPhone) {
        if (current.phone === savedPhone || (current.phone && loginInProgress)) return current
        return { ...current, phone: savedPhone, error: '' }
      }
      if (!loginInProgress && current.phone) return { ...current, phone: '' }
      return current
    })
  }, [
    activeProvider,
    selectedModelId,
    selectedProviderLink?.id,
    selectedProviderLink?.external_id,
    selectedProviderLink?.username,
    selectedProviderLink?.email,
  ])

  const updateOnlyfansapi = (patch) => setOnlyfansapi((current) => ({ ...current, ...patch }))
  const updateOnlymonster = (patch) => setOnlymonster((current) => ({ ...current, ...patch }))
  const updateFansly = (patch) => setFansly((current) => ({ ...current, ...patch }))
  const updateFanltvOfapi = (patch) => setFanltvOfapi((current) => ({ ...current, ...patch }))
  const updateFanvuePlatform = (patch) => setFanvuePlatform((current) => ({ ...current, ...patch }))
  const updateFanvue = (patch) => setFanvue((current) => ({ ...current, ...patch }))
  const updateFanvueSettings = (patch) => setFanvueSettings((current) => ({ ...current, ...patch }))
  const updateInstagram = (patch) => setInstagram((current) => ({ ...current, ...patch }))
  const updateInstagramSettings = (patch) => setInstagramSettings((current) => ({ ...current, ...patch }))
  const updateFanvueSync = (patch) => setFanvueSync((current) => ({ ...current, ...patch }))
  const updateFanslySync = (patch) => setFanslySync((current) => ({ ...current, ...patch }))
  const updateFanslyDirect = (patch) => setFanslyDirect((current) => ({ ...current, ...patch }))
  const updateTelegram = (patch) => setTelegram((current) => ({ ...current, ...patch }))
  const updateTelegramSettings = (patch) => setTelegramSettings((current) => ({ ...current, ...patch }))
  const updateTributeSettings = (patch) => setTributeSettings((current) => ({ ...current, ...patch }))
  const updateTelegramPayments = (patch) => setTelegramPayments((current) => ({ ...current, ...patch }))
  const updateAigfSettings = (patch) => setAigfSettings((current) => ({ ...current, ...patch }))

  useEffect(() => {
    if (!fansly.authPolling || !fansly.accountId || !selectedModelId) return undefined
    const refresh = () => handleFanslyAuthStatus({ quiet: true })
    const initialTimer = window.setTimeout(refresh, 800)
    const interval = window.setInterval(refresh, 3000)
    return () => {
      window.clearTimeout(initialTimer)
      window.clearInterval(interval)
    }
  }, [fansly.authPolling, fansly.accountId, fansly.apiKey, selectedModelId])

  function handleTelegramCodeChange(event) {
    updateTelegram({ code: event.target.value.replace(/\D/g, '').slice(0, 8), error: '' })
  }

  function handleProviderSelect(providerKey) {
    setActiveProvider(providerKey)
    window.requestAnimationFrame(() => {
      connectionPanelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  function mergeUpdatedModel(updated) {
    if (!updated?.id) return
    setModels((current) => current.map((model) => (model.id === updated.id ? { ...model, ...updated } : model)))
  }

  async function handleHealthCheck(modelId, linkId) {
    if (!modelId || !linkId) return
    const busyKey = `${modelId}:${linkId}`
    setHealthCheck({ busyKey, message: '', error: '' })
    try {
      const updated = await checkProviderConnectionHealth(modelId, linkId)
      mergeUpdatedModel(updated)
      const checkedLink = providerLinks(updated).find((item) => String(item.id) === String(linkId))
      const status = providerStatus(checkedLink)
      const providerName = providerLabel(checkedLink?.provider)
      setHealthCheck({ busyKey: '', message: `${providerName} health check finished: ${status}.`, error: '' })
    } catch (err) {
      setHealthCheck({ busyKey: '', message: '', error: getApiErrorMessage(err, 'Failed to check provider health') })
    }
  }

  async function handleOnlyFansApiConnect(event) {
    event.preventDefault()
    if (!selectedModelId || (!onlyfansapi.accountId.trim() && !onlyfansapi.apiKey.trim())) return
    updateOnlyfansapi({ busy: true, error: '', message: '' })
    try {
      const updated = await connectModelOnlyfansApiAccount(selectedModelId, {
        apiKey: onlyfansapi.apiKey.trim() || undefined,
        accountId: onlyfansapi.accountId.trim(),
        connectionId: onlyfansapi.connectionId.trim() || undefined,
        email: onlyfansapi.email.trim() || undefined,
      })
      mergeUpdatedModel(updated)
      updateOnlyfansapi({ apiKey: '', accountId: '', connectionId: '', email: '', message: 'OnlyFansAPI account connected.' })
    } catch (err) {
      updateOnlyfansapi({ error: getApiErrorMessage(err, 'Failed to connect OnlyFansAPI account') })
    } finally {
      updateOnlyfansapi({ busy: false })
    }
  }

  async function handleOnlyMonsterConnect(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateOnlymonster({ busy: true, error: '', message: '' })
    try {
      const updated = await connectModelOnlyMonsterAccount(selectedModelId, {
        apiToken: onlymonster.apiToken.trim() || undefined,
        accountId: onlymonster.accountId.trim(),
        platformAccountId: onlymonster.platformAccountId.trim() || undefined,
        username: onlymonster.username.trim() || undefined,
        email: onlymonster.email.trim() || undefined,
      })
      mergeUpdatedModel(updated)
      updateOnlymonster({ apiToken: '', accountId: '', platformAccountId: '', username: '', email: '', message: 'OnlyMonster account connected.' })
    } catch (err) {
      updateOnlymonster({ error: getApiErrorMessage(err, 'Failed to connect OnlyMonster account') })
    } finally {
      updateOnlymonster({ busy: false })
    }
  }

  async function handleFanslyAccountsLoad(event) {
    event?.preventDefault()
    if (!selectedModelId) return
    updateFansly({ authBusy: true, error: '', message: '' })
    try {
      const result = await listFanslyAuthAccounts(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
      })
      const accounts = asList(result?.accounts)
      const selected = accounts.find((item) => item.id === fansly.accountId) || (accounts.length === 1 ? accounts[0] : null)
      updateFansly({
        accounts,
        accountId: selected?.id || fansly.accountId,
        authStatus: selected,
        message: accounts.length ? `Found ${accounts.length} Fansly account${accounts.length === 1 ? '' : 's'}.` : 'The key is valid, but it has no Fansly accounts yet.',
      })
    } catch (err) {
      updateFansly({ error: getApiErrorMessage(err, 'Failed to validate OnlyFansAPI key') })
    } finally {
      updateFansly({ authBusy: false })
    }
  }

  async function handleFanslyAuthStart(event) {
    event.preventDefault()
    if (!selectedModelId || !fansly.username.trim() || !fansly.password) return
    updateFansly({ authBusy: true, authPolling: false, authStatus: null, error: '', message: '' })
    try {
      const result = await startFanslyAuthentication(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
        username: fansly.username.trim(),
        password: fansly.password,
        name: fansly.email.trim() || modelTitle(selectedModel),
        proxyCountry: fansly.proxyCountry || undefined,
        forceConnect: true,
      })
      const accountId = String(result?.account_id || '').trim()
      if (!accountId) throw new Error('OnlyFansAPI did not return a Fansly account ID')
      updateFansly({
        accountId,
        password: '',
        authStatus: result,
        authPolling: true,
        message: 'Fansly authentication started. Waiting for verification status…',
      })
    } catch (err) {
      updateFansly({ error: getApiErrorMessage(err, 'Failed to start Fansly authentication') })
    } finally {
      updateFansly({ authBusy: false })
    }
  }

  async function handleFanslyAuthStatus({ quiet = false } = {}) {
    if (!selectedModelId || !fansly.accountId || fanslyAuthCheckRef.current) return
    fanslyAuthCheckRef.current = true
    if (!quiet) updateFansly({ authBusy: true, error: '' })
    try {
      const result = await getFanslyAuthenticationStatus(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
        accountId: fansly.accountId,
      })
      if (result?.is_authenticated) {
        updateFansly({ authPolling: false, authStatus: result, message: 'Fansly authenticated. Saving the connection…' })
        const updated = await connectModelFanslyAccount(selectedModelId, {
          apiKey: fansly.apiKey.trim() || undefined,
          accountId: fansly.accountId,
          email: fansly.email.trim() || undefined,
        })
        mergeUpdatedModel(updated)
        updateFansly({
          authPolling: false,
          authStatus: result,
          apiKey: '',
          password: '',
          code: '',
          message: 'Fansly account authenticated and connected.',
          error: '',
        })
        return
      }
      const statusText = String(result?.status || '').toLowerCase()
      const needsCode = Boolean(result?.needs_code)
      const failed = statusText.includes('fail') || Boolean(result?.last_error)
      let message = `Fansly status: ${result?.status || 'pending'}.`
      if (needsCode) {
        message = result?.twofa_type === 2
          ? `Enter the code sent to ${result?.masked_email || 'the Fansly email'}.`
          : 'Enter the current code from the authenticator app.'
      }
      updateFansly({
        authStatus: result,
        authPolling: !needsCode && !failed,
        message,
        error: failed ? (result?.last_error || `Fansly authentication failed: ${result?.status || 'unknown error'}`) : '',
      })
    } catch (err) {
      updateFansly({ authPolling: false, error: getApiErrorMessage(err, 'Failed to check Fansly authentication') })
    } finally {
      fanslyAuthCheckRef.current = false
      if (!quiet) updateFansly({ authBusy: false })
    }
  }

  async function handleFanslyTwoFactor(event) {
    event.preventDefault()
    if (!selectedModelId || !fansly.accountId || !fansly.code.trim()) return
    updateFansly({ authBusy: true, authPolling: false, error: '', message: '' })
    try {
      const result = await submitFanslyAuthenticationCode(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
        accountId: fansly.accountId,
        code: fansly.code.trim(),
      })
      updateFansly({ code: '', authStatus: result, authPolling: true, message: 'Verification code submitted. Checking the account…' })
    } catch (err) {
      updateFansly({ error: getApiErrorMessage(err, 'Failed to submit Fansly verification code') })
    } finally {
      updateFansly({ authBusy: false })
    }
  }

  async function handleFanslyReauthenticate(event) {
    event.preventDefault()
    if (!selectedModelId || !fansly.accountId) return
    updateFansly({ authBusy: true, authPolling: false, error: '', message: '' })
    try {
      const result = await reauthenticateFanslyAccount(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
        accountId: fansly.accountId,
      })
      updateFansly({ authStatus: result, authPolling: true, message: 'Fansly re-authentication started. Waiting for verification status…' })
    } catch (err) {
      updateFansly({ error: getApiErrorMessage(err, 'Failed to re-authenticate Fansly account') })
    } finally {
      updateFansly({ authBusy: false })
    }
  }

  async function handleFanslyConnect(event) {
    event.preventDefault()
    if (!selectedModelId || (!fansly.accountId.trim() && !fansly.username.trim() && !fansly.fanslyId.trim() && !fansly.apiKey.trim())) return
    updateFansly({ busy: true, error: '', message: '' })
    try {
      const updated = await connectModelFanslyAccount(selectedModelId, {
        apiKey: fansly.apiKey.trim() || undefined,
        accountId: fansly.accountId.trim(),
        fanslyId: fansly.fanslyId.trim() || undefined,
        username: fansly.username.trim() || undefined,
        email: fansly.email.trim() || undefined,
      })
      mergeUpdatedModel(updated)
      updateFansly({ apiKey: '', password: '', code: '', authPolling: false, message: 'Fansly account connected.' })
    } catch (err) {
      updateFansly({ error: getApiErrorMessage(err, 'Failed to connect Fansly account') })
    } finally {
      updateFansly({ busy: false })
    }
  }

  async function handleFanltvOfapiConnect(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateFanltvOfapi({ busy: true, error: '', message: '' })
    try {
      const updated = await connectModelFanltvOfapiAccount(selectedModelId, {
        apiKey: fanltvOfapi.apiKey.trim() || undefined,
        accountId: fanltvOfapi.accountId.trim(),
        connectionId: fanltvOfapi.connectionId.trim() || undefined,
        email: fanltvOfapi.email.trim() || undefined,
      })
      mergeUpdatedModel(updated)
      updateFanltvOfapi({ apiKey: '', accountId: '', connectionId: '', email: '', message: 'FanLTV OFAPI backup connected.' })
    } catch (err) {
      updateFanltvOfapi({ error: getApiErrorMessage(err, 'Failed to connect FanLTV OFAPI backup') })
    } finally {
      updateFanltvOfapi({ busy: false })
    }
  }

  async function handleFanvueStart(includeInsights = false) {
    if (!selectedModelId) return
    const popup = window.open('', '_blank')
    if (popup) {
      try {
        popup.document.write('<!doctype html><title>Fanvue OAuth</title><p>Opening Fanvue OAuth...</p>')
      } catch (_) {}
    }
    updateFanvue({ busy: true, error: '', message: 'Creating Fanvue OAuth link...', authUrl: '' })
    try {
      const result = await startFanvueOAuth(selectedModelId, { includeInsights: includeInsights === true })
      const authUrl = result?.auth_url || result?.authUrl || ''
      const state = result?.state || ''
      rememberFanvueOAuthSession({ state, modelId: selectedModelId, authUrl })
      if (authUrl && popup && !popup.closed) {
        try { popup.opener = null } catch (_) {}
        popup.location.href = authUrl
        updateFanvue({ authUrl, state, message: 'Fanvue OAuth opened in a new tab. If the tab did not open, use Open OAuth link.' })
      } else if (authUrl) {
        updateFanvue({ authUrl, state, message: 'OAuth link created. Your browser blocked the popup, so use Open OAuth link.' })
      } else {
        if (popup && !popup.closed) popup.close()
        updateFanvue({ authUrl, state, message: 'OAuth session created. Complete the callback from Fanvue to finish.' })
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close()
      updateFanvue({ error: getApiErrorMessage(err, 'Failed to start Fanvue OAuth'), message: '' })
    } finally {
      updateFanvue({ busy: false })
    }
  }

  async function handleFanvuePlatformStart(includeInsights = false) {
    if (!selectedModelId) return
    const popup = window.open('', '_blank')
    if (popup) {
      try {
        popup.document.write('<!doctype html><title>Fanvue OAuth</title><p>Opening Fanvue OAuth...</p>')
      } catch (_) {}
    }
    updateFanvuePlatform({ busy: true, error: '', message: 'Creating Fanvue app OAuth link...', authUrl: '' })
    try {
      const result = await startFanvuePlatformOAuth(selectedModelId, { includeInsights: includeInsights === true })
      const authUrl = result?.auth_url || result?.authUrl || ''
      const state = result?.state || ''
      rememberFanvueOAuthSession({ state, modelId: selectedModelId, authUrl })
      if (authUrl && popup && !popup.closed) {
        try { popup.opener = null } catch (_) {}
        popup.location.href = authUrl
        updateFanvuePlatform({ authUrl, state, message: 'Fanvue app OAuth opened in a new tab. If the tab did not open, use Open OAuth link.' })
      } else if (authUrl) {
        updateFanvuePlatform({ authUrl, state, message: 'OAuth link created. Your browser blocked the popup, so use Open OAuth link.' })
      } else {
        if (popup && !popup.closed) popup.close()
        updateFanvuePlatform({ authUrl, state, message: 'OAuth session created. Complete the callback from Fanvue to finish.' })
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close()
      updateFanvuePlatform({ error: getApiErrorMessage(err, 'Failed to start Fanvue platform app OAuth'), message: '' })
    } finally {
      updateFanvuePlatform({ busy: false })
    }
  }

  async function handleFanvueSettingsSave(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateFanvueSettings({ saving: true, error: '', message: '' })
    try {
      const result = await updateFanvueOAuthSettings(selectedModelId, {
        clientId: fanvueSettings.clientId.trim(),
        clientSecret: fanvueSettings.clientSecret,
        redirectUri: fanvueSettings.redirectUri.trim() || defaultFanvueRedirectUri(),
        authBaseUrl: fanvueSettings.authBaseUrl.trim(),
        apiBaseUrl: fanvueSettings.apiBaseUrl.trim(),
        apiVersion: fanvueSettings.apiVersion.trim(),
        oauthScopes: fanvueSettings.oauthScopes.trim(),
        webhookSecret: fanvueSettings.webhookSecret,
      })
      updateFanvueSettings({
        saving: false,
        clientId: result?.client_id || '',
        clientSecret: '',
        clientSecretSet: Boolean(result?.client_secret_set),
        redirectUri: result?.redirect_uri || defaultFanvueRedirectUri(),
        authBaseUrl: result?.auth_base_url || '',
        apiBaseUrl: result?.api_base_url || '',
        apiVersion: result?.api_version || '',
        oauthScopes: result?.oauth_scopes || '',
        webhookSecret: '',
        webhookSecretSet: Boolean(result?.webhook_secret_set),
        webhookUrl: result?.webhook_url || '',
        configured: Boolean(result?.configured),
        source: result?.source || 'model',
        message: 'Fanvue OAuth settings saved for this model.',
      })
    } catch (err) {
      updateFanvueSettings({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to save Fanvue OAuth settings'),
      })
    }
  }

  async function handleInstagramSettingsSave(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateInstagramSettings({ saving: true, error: '', message: '' })
    try {
      const callbackUrl = instagramSettings.callbackUrl || defaultInstagramRedirectUri()
      const result = await updateInstagramOAuthSettings(selectedModelId, {
        clientId: instagramSettings.clientId.trim(),
        clientSecret: instagramSettings.clientSecret,
        metaAppId: instagramSettings.metaAppId.trim(),
        metaAppSecret: instagramSettings.metaAppSecret,
        redirectUri: instagramSettings.redirectUri.trim() || callbackUrl,
        authBaseUrl: instagramSettings.authBaseUrl.trim(),
        tokenUrl: instagramSettings.tokenUrl.trim(),
        graphBaseUrl: instagramSettings.graphBaseUrl.trim(),
        oauthScopes: instagramSettings.oauthScopes.trim(),
      })
      updateInstagramSettings({
        saving: false,
        clientId: result?.client_id || '',
        clientSecret: '',
        clientSecretSet: Boolean(result?.client_secret_set),
        metaAppId: result?.meta_app_id || '',
        metaAppSecret: '',
        metaAppSecretSet: Boolean(result?.meta_app_secret_set),
        redirectUri: result?.redirect_uri || result?.callback_url || callbackUrl,
        callbackUrl: result?.callback_url || callbackUrl,
        webhookUrl: result?.webhook_url || '',
        webhookVerifyToken: result?.webhook_verify_token || '',
        authBaseUrl: result?.auth_base_url || 'https://www.instagram.com/oauth',
        tokenUrl: result?.token_url || 'https://api.instagram.com/oauth/access_token',
        graphBaseUrl: result?.graph_base_url || 'https://graph.instagram.com',
        oauthScopes: result?.oauth_scopes || '',
        configured: Boolean(result?.configured),
        source: result?.source || 'model',
        message: 'Instagram OAuth settings saved for this model.',
      })
    } catch (err) {
      updateInstagramSettings({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to save Instagram OAuth settings'),
      })
    }
  }

  async function handleInstagramStart() {
    if (!selectedModelId) return
    const popup = window.open('', '_blank')
    if (popup) {
      try {
        popup.document.write('<!doctype html><title>Instagram OAuth</title><p>Opening Instagram OAuth...</p>')
      } catch (_) {}
    }
    updateInstagram({ busy: true, error: '', message: 'Creating Instagram OAuth link...', authUrl: '' })
    try {
      const result = await startInstagramOAuth(selectedModelId)
      const authUrl = result?.auth_url || result?.authUrl || ''
      const state = result?.state || ''
      if (authUrl && popup && !popup.closed) {
        try { popup.opener = null } catch (_) {}
        popup.location.href = authUrl
        updateInstagram({ authUrl, state, message: 'Instagram OAuth opened in a new tab. If the tab did not open, use Open OAuth link.' })
      } else if (authUrl) {
        updateInstagram({ authUrl, state, message: 'OAuth link created. Your browser blocked the popup, so use Open OAuth link.' })
      } else {
        if (popup && !popup.closed) popup.close()
        updateInstagram({ authUrl, state, message: 'OAuth session created. Complete the callback from Instagram to finish.' })
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close()
      updateInstagram({ error: getApiErrorMessage(err, 'Failed to start Instagram OAuth'), message: '' })
    } finally {
      updateInstagram({ busy: false })
    }
  }

  async function handleFanvueComplete(event) {
    event.preventDefault()
    if (!selectedModelId || !fanvue.code.trim()) return
    updateFanvue({ busy: true, error: '', message: '' })
    try {
      const updated = await completeFanvueOAuth(selectedModelId, {
        code: fanvue.code.trim(),
        state: fanvue.state.trim() || undefined,
        creatorUserUuid: fanvue.creatorUserUuid.trim() || undefined,
      })
      mergeUpdatedModel(updated)
      updateFanvue({ code: '', creatorUserUuid: '', message: 'Fanvue account connected.' })
    } catch (err) {
      updateFanvue({ error: getApiErrorMessage(err, 'Failed to complete Fanvue OAuth') })
    } finally {
      updateFanvue({ busy: false })
    }
  }

  async function handleFanvueSync(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateFanvueSync({ busy: true, error: '', message: '' })
    try {
      const chatUuids = fanvueSync.chatUuids
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean)
      const result = await syncFanvueMessages(selectedModelId, {
        chatUuids,
        sinceMessageUuid: fanvueSync.sinceMessageUuid.trim() || undefined,
        limit: Number(fanvueSync.limit) || 20,
        maxChats: Number(fanvueSync.maxChats) || 20,
        enqueueIncoming: fanvueSync.enqueueIncoming,
      })
      const created = Number(result?.messages_created || 0)
      const updated = Number(result?.messages_updated || 0)
      const chats = Number(result?.chats_requested || 0)
      updateFanvueSync({ message: `Synced ${created + updated} messages across ${chats} chats.` })
      getModels().then((payload) => setModels(asList(payload))).catch(() => {})
    } catch (err) {
      updateFanvueSync({ error: getApiErrorMessage(err, 'Failed to sync Fanvue messages') })
    } finally {
      updateFanvueSync({ busy: false })
    }
  }

  async function handleFanslySync(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateFanslySync({ busy: true, error: '', message: '' })
    try {
      const result = await syncFanslyMessages(selectedModelId, {
        limit: Number(fanslySync.limit) || 25,
        maxChats: Number(fanslySync.maxChats) || 25,
        enqueueUnanswered: fanslySync.enqueueUnanswered,
      })
      const created = Number(result?.messages_created || 0)
      const updated = Number(result?.messages_updated || 0)
      const enqueued = Number(result?.incoming_enqueued || 0)
      const chats = Number(result?.chats_requested || 0)
      const errors = Array.isArray(result?.errors) ? result.errors.length : 0
      const credits = Number(result?.credit_metadata?.used_total || 0)
      if (result?.skipped) {
        const retryAt = result?.circuit?.retry_at
        updateFanslySync({
          error: `Fansly sync is paused after a provider ${result?.circuit?.status_code || ''} error${retryAt ? ` until ${new Date(retryAt).toLocaleString()}` : ''}.`,
        })
      } else {
        const summary = `Synced ${created + updated} messages across ${chats} chats. Queued ${enqueued}. API credits used: ${credits}.`
        updateFanslySync(errors
          ? { message: '', error: `${summary} ${errors} chat${errors === 1 ? '' : 's'} could not be fully synced.` }
          : { message: summary, error: '' })
      }
      getModels().then((payload) => setModels(asList(payload))).catch(() => {})
    } catch (err) {
      updateFanslySync({ error: getApiErrorMessage(err, 'Failed to sync Fansly messages') })
    } finally {
      updateFanslySync({ busy: false })
    }
  }

  async function handleFanslyDirectSave(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateFanslyDirect({ saving: true, error: '', message: '' })
    try {
      const result = await updateFanslyDirectSettings(selectedModelId, {
        sessionToken: fanslyDirect.sessionToken.trim() || undefined,
        enabled: fanslyDirect.enabled,
        verifyNow: true,
      })
      updateFanslyDirect({
        saving: false,
        sessionToken: '',
        sessionTokenSet: Boolean(result?.session_token_set),
        configured: Boolean(result?.configured),
        enabled: Boolean(result?.enabled),
        verified: Boolean(result?.verified),
        accountId: result?.account_id || '',
        username: result?.username || '',
        displayName: result?.display_name || '',
        lastVerifiedAt: result?.last_verified_at || '',
        message: result?.enabled ? 'Direct Fansly upload enabled.' : 'Fansly web session verified.',
        error: result?.health_error || '',
      })
    } catch (err) {
      updateFanslyDirect({ saving: false, error: getApiErrorMessage(err, 'Failed to verify Fansly web session') })
    }
  }

  async function handleFanslyDirectClear() {
    if (!selectedModelId) return
    updateFanslyDirect({ saving: true, error: '', message: '' })
    try {
      await updateFanslyDirectSettings(selectedModelId, { clearSession: true, verifyNow: false })
      updateFanslyDirect({
        saving: false,
        sessionToken: '',
        sessionTokenSet: false,
        configured: false,
        enabled: false,
        verified: false,
        accountId: '',
        username: '',
        displayName: '',
        lastVerifiedAt: '',
        message: 'Direct Fansly session removed.',
        error: '',
      })
    } catch (err) {
      updateFanslyDirect({ saving: false, error: getApiErrorMessage(err, 'Failed to remove Fansly web session') })
    }
  }

  async function handleTelegramStart(event) {
    event.preventDefault()
    if (!selectedModelId || !telegram.phone.trim()) return
    updateTelegram({ busy: true, error: '', message: '', code: '', password: '', codeSent: false, passwordRequired: false })
    try {
      await startTelegramAccountLogin(selectedModelId, { phone: telegram.phone.trim() })
      updateTelegram({ codeSent: true, message: 'Telegram sent a login code to the model account. Enter it below.' })
    } catch (err) {
      updateTelegram({ error: getApiErrorMessage(err, 'Failed to start Telegram login') })
    } finally {
      updateTelegram({ busy: false })
    }
  }

  async function handleTelegramSettingsSave(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateTelegramSettings({ saving: true, error: '', message: '' })
    try {
      const result = await updateTelegramAccountAppSettings(selectedModelId, {
        apiId: telegramSettings.apiId.trim(),
        apiHash: telegramSettings.apiHash.trim() || undefined,
      })
      updateTelegramSettings({
        apiHash: '',
        apiHashSet: Boolean(result?.api_hash_set),
        configured: Boolean(result?.configured),
        source: result?.source || '',
        message: 'Telegram API settings saved.',
      })
    } catch (err) {
      updateTelegramSettings({ error: getApiErrorMessage(err, 'Failed to save Telegram API settings') })
    } finally {
      updateTelegramSettings({ saving: false })
    }
  }

  async function saveTributeSettings({ verifyNow = false } = {}) {
    if (!selectedModelId) return
    updateTributeSettings({ saving: true, error: '', message: '' })
    try {
      const result = await updateTelegramTributeSettings(selectedModelId, {
        apiKey: tributeSettings.apiKey.trim() || undefined,
        externalId: tributeSettings.externalId.trim() || undefined,
        isActive: true,
        verifyNow,
      })
      updateTributeSettings({
        saving: false,
        apiKey: '',
        apiKeySet: Boolean(result?.api_key_set),
        externalId: result?.external_id || '',
        webhookUrl: result?.webhook_url || '',
        configured: Boolean(result?.configured),
        connected: Boolean(result?.connected),
        productsProbeCount: result?.products_probe_count ?? null,
        lastHealthCheckAt: result?.last_health_check_at || '',
        message: result?.connected ? 'Telegram Tribute key verified and saved.' : 'Telegram Tribute settings saved.',
        error: result?.health_error || '',
      })
      getModels().then((payload) => setModels(asList(payload))).catch(() => {})
    } catch (err) {
      updateTributeSettings({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to save Telegram Tribute settings'),
      })
    }
  }

  function handleTributeSettingsSave(event) {
    event.preventDefault()
    saveTributeSettings({ verifyNow: false })
  }

  async function saveTelegramPaymentsSettings({ setWebhook = false } = {}) {
    if (!selectedModelId) return
    updateTelegramPayments({ saving: true, error: '', message: '', invoiceLink: '' })
    try {
      const result = await updateTelegramBotPaymentsSettings(selectedModelId, {
        botToken: telegramPayments.botToken.trim() || undefined,
        businessConnectionId: telegramPayments.businessConnectionId.trim() || undefined,
        isActive: true,
        verifyNow: true,
        setWebhook,
      })
      updateTelegramPayments({
        saving: false,
        botToken: '',
        botTokenSet: Boolean(result?.bot_token_set),
        botUsername: result?.bot_username || '',
        botId: result?.bot_id || '',
        businessConnectionId: result?.business_connection_id || '',
        webhookUrl: result?.webhook_url || '',
        webhookSecretSet: Boolean(result?.webhook_secret_set),
        configured: Boolean(result?.configured),
        connected: Boolean(result?.connected),
        lastHealthCheckAt: result?.last_health_check_at || '',
        message: setWebhook ? 'Telegram Stars bot verified and webhook configured.' : 'Telegram Stars bot settings saved.',
        error: result?.health_error || '',
      })
      getModels().then((payload) => setModels(asList(payload))).catch(() => {})
    } catch (err) {
      updateTelegramPayments({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to save Telegram Bot Payments settings'),
      })
    }
  }

  function handleTelegramPaymentsSettingsSave(event) {
    event.preventDefault()
    saveTelegramPaymentsSettings({ setWebhook: false })
  }

  async function handleTelegramInvoiceLink(event) {
    event.preventDefault()
    if (!selectedModelId) return
    updateTelegramPayments({ saving: true, error: '', message: '', invoiceLink: '' })
    try {
      const result = await createTelegramBotPaymentInvoiceLink(selectedModelId, {
        title: telegramPayments.invoiceTitle.trim(),
        description: telegramPayments.invoiceDescription.trim(),
        starCount: Number(telegramPayments.invoiceStars) || 1,
        payload: telegramPayments.invoicePayload.trim() || undefined,
        photoUrl: telegramPayments.invoicePhotoUrl.trim() || undefined,
      })
      updateTelegramPayments({
        saving: false,
        invoiceLink: result?.invoice_link || '',
        invoicePayload: result?.payload || telegramPayments.invoicePayload,
        message: 'Telegram Stars invoice link created.',
      })
    } catch (err) {
      updateTelegramPayments({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to create Telegram invoice link'),
      })
    }
  }

  async function saveAigfSettings({ generateToken = false } = {}) {
    if (!selectedModelId) return
    updateAigfSettings({ saving: true, error: '', message: '', apiTokenOnce: '' })
    try {
      const result = await updateAigirlfactorySettings(selectedModelId, {
        personaKey: aigfSettings.personaKey.trim(),
        channel: aigfSettings.channel.trim(),
        apiToken: aigfSettings.apiToken.trim() || undefined,
        generateToken,
        requireSignature: Boolean(aigfSettings.requireSignature),
        isActive: true,
      })
      updateAigfSettings({
        saving: false,
        apiToken: '',
        apiTokenSet: Boolean(result?.api_token_set),
        apiTokenOnce: result?.api_token_once || '',
        endpointUrl: result?.endpoint_url || result?.endpoint_path || aigfSettings.endpointUrl,
        configured: Boolean(result?.configured),
        connected: Boolean(result?.connected),
        message: generateToken ? 'AIGirlFactory token generated. Copy it now.' : 'AIGirlFactory channel settings saved.',
      })
      getModels().then((payload) => setModels(asList(payload))).catch(() => {})
    } catch (err) {
      updateAigfSettings({
        saving: false,
        error: getApiErrorMessage(err, 'Failed to save AIGirlFactory settings'),
      })
    }
  }

  function handleAigfSettingsSave(event) {
    event.preventDefault()
    saveAigfSettings({ generateToken: false })
  }

  async function handleTelegramComplete(event) {
    event.preventDefault()
    const hasCode = Boolean(telegram.code.trim())
    const hasPassword = Boolean(telegram.password)
    if (!selectedModelId || !telegram.phone.trim() || (!hasCode && !(telegram.passwordRequired && hasPassword))) return
    updateTelegram({ busy: true, error: '', message: '' })
    try {
      const result = await completeTelegramAccountLogin(selectedModelId, {
        phone: telegram.phone.trim(),
        code: hasCode ? telegram.code.trim() : undefined,
        password: telegram.password || undefined,
      })
      if (result?.password_required || result?.passwordRequired) {
        updateTelegram({ passwordRequired: true, message: 'Two-step password is required for this Telegram account.' })
      } else {
        mergeUpdatedModel(result)
        updateTelegram({ code: '', password: '', codeSent: false, passwordRequired: false, message: 'Telegram account connected. Automation can now send as this account.' })
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to complete Telegram login')
      const expired = /expired|start login again|send telegram code again/i.test(message)
      updateTelegram({
        error: message,
        code: expired ? '' : telegram.code,
        password: expired ? '' : telegram.password,
        codeSent: expired ? false : telegram.codeSent,
        passwordRequired: expired ? false : telegram.passwordRequired,
      })
    } finally {
      updateTelegram({ busy: false })
    }
  }

  if (loading) return <LoadingBlock label="Loading provider connections..." />

  return (
    <div className="v2-stack v2-connections-page">
      <Section eyebrow="Provider layer" title="Connections" description="Choose the model, click the service, and finish the connection from the same workspace.">
        {error ? <div className="v2-error">{error}</div> : null}
        <div className="v2-connection-workspace">
          <div className="v2-provider-picker" aria-label="Provider services">
            <label className="v2-field v2-provider-model-select">
              <span>Model</span>
              <select value={selectedModelId} onChange={(event) => setSelectedModelId(event.target.value)}>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>{modelTitle(model)}</option>
                ))}
              </select>
            </label>
            {visibleProviderCatalog.map((provider) => {
              const link = selectedModel ? providerLinks(selectedModel).find((item) => linkMatchesProvider(item, provider.key)) : null
              const status = provider.state === 'planned' ? 'planned' : link ? providerStatus(link) : 'not connected'
              const statusLabel = connectionStatusLabel(link, status)
              const active = provider.key === activeProvider
              return (
                <button
                  key={provider.key}
                  type="button"
                  className={`v2-provider-card v2-provider-card-button ${active ? 'is-active' : ''}`.trim()}
                  aria-pressed={active}
                  onClick={() => handleProviderSelect(provider.key)}
                >
                  <div>
                    <span className="v2-provider-logo">{provider.name.slice(0, 2).toUpperCase()}</span>
                    <h3>{provider.name}</h3>
                    <p>{provider.description}</p>
                    <div className="v2-provider-card-meta">
                      <span>{provider.actionLabel}</span>
                      <em>{selectedModel ? modelTitle(selectedModel) : 'No model selected'}</em>
                    </div>
                  </div>
                  <StatusPill tone={provider.state === 'planned' ? 'neutral' : statusTone(status)}>{statusLabel}</StatusPill>
                </button>
              )
            })}
          </div>

          <article className="v2-connection-panel" ref={connectionPanelRef}>
            <div className="v2-card-heading">
              <div>
                <p className="v2-eyebrow">{selectedProvider.name}</p>
                <h3>{selectedProvider.actionLabel}</h3>
              </div>
              <StatusPill tone={selectedProvider.state === 'planned' ? 'neutral' : statusTone(selectedProviderStatus)}>{selectedProviderStatusLabel}</StatusPill>
            </div>
            <div className="v2-quick-summary">
              <span>Selected model</span>
              <strong>{selectedModel ? modelTitle(selectedModel) : 'No model selected'}</strong>
            </div>
            <ol className="v2-instruction-list">
              {selectedProvider.instructions.map((item) => <li key={item}>{item}</li>)}
            </ol>

            {['fanvue', 'fanvue_platform', 'fanvue_store'].includes(activeProvider) && selectedModelId ? (
              <FanvueFinancialHistory key={selectedModelId} modelId={selectedModelId}
                authorizationBusy={fanvue.busy || fanvuePlatform.busy}
                onAuthorize={() => activeProvider === 'fanvue' ? handleFanvueStart(true) : handleFanvuePlatformStart(true)} />
            ) : null}

            {activeProvider === 'fanvue_store' && (
              <div className="v2-connection-panel-stack">
                <div className="v2-config-card v2-form-grid">
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Fanvue App Store</p>
                      <h3>Creator connection</h3>
                    </div>
                    <StatusPill tone={statusTone(selectedProviderStatus)}>{selectedProviderStatus}</StatusPill>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Access</span>
                    <strong>Chats, fans and selected media through secure OAuth</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Automation</span>
                    <strong>{selectedModel?.bot_paused ? 'Paused until you enable it' : 'Enabled for this model'}</strong>
                  </div>
                </div>
                <div className="v2-action-row">
                  <button
                    type="button"
                    className="v2-button"
                    disabled={!selectedModelId || fanvuePlatform.busy || !fanvuePlatformConfigured}
                    onClick={handleFanvuePlatformStart}
                  >
                    {fanvuePlatform.busy ? 'Opening Fanvue...' : 'Reconnect Fanvue'}
                  </button>
                  <Link className="v2-text-link" to={`/v2/models/${selectedModelId}`}>Review model setup</Link>
                </div>
                <details className="v2-advanced-disclosure">
                  <summary>Sync recent messages</summary>
                  <form className="v2-form-grid" onSubmit={handleFanvueSync}>
                    <label className="v2-field">
                      <span>Messages per chat</span>
                      <input type="number" min="1" max="50" value={fanvueSync.limit} onChange={(event) => updateFanvueSync({ limit: event.target.value })} />
                    </label>
                    <label className="v2-field">
                      <span>Maximum chats</span>
                      <input type="number" min="1" max="50" value={fanvueSync.maxChats} onChange={(event) => updateFanvueSync({ maxChats: event.target.value })} />
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={fanvueSync.busy}>
                      {fanvueSync.busy ? 'Syncing...' : 'Sync messages'}
                    </button>
                  </form>
                </details>
                {fanvueSync.message ? <div className="v2-success">{fanvueSync.message}</div> : null}
                {fanvueSync.error ? <div className="v2-error">{fanvueSync.error}</div> : null}
              </div>
            )}

            {activeProvider === 'fanvue_platform' && (
              <div className="v2-connection-panel-stack">
                <div className="v2-config-card v2-form-grid">
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Fanvue platform app</p>
                      <h3>Shared OAuth app</h3>
                    </div>
                    <StatusPill tone={fanvuePlatformConfigured ? 'good' : 'warn'}>
                      {fanvuePlatformConfigured ? 'ready' : 'missing'}
                    </StatusPill>
                  </div>
                  <div className="v2-quick-summary">
                    <span>Client ID</span>
                    <strong>{fanvuePlatformSettings.client_id || 'Not configured'}</strong>
                  </div>
                  <div className="v2-quick-summary">
                    <span>Client secret</span>
                    <strong>{fanvuePlatformSettings.client_secret_set ? 'Saved on server' : 'Not configured'}</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Redirect URI</span>
                    <strong>{fanvuePlatformSettings.redirect_uri || defaultFanvueRedirectUri()}</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Webhook URL</span>
                    <strong>{fanvuePlatformSettings.webhook_url || 'Set WEBHOOK_BASE_URL on the server to generate webhook URL'}</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Scopes</span>
                    <strong>{fanvuePlatformSettings.oauth_scopes || 'openid offline_access read:self read:chat write:chat'}</strong>
                  </div>
                </div>
                <div className="v2-action-row">
                  <button
                    type="button"
                    className="v2-button"
                    disabled={!selectedModelId || fanvuePlatform.busy || fanvuePlatform.settingsBusy || !fanvuePlatformConfigured}
                    onClick={handleFanvuePlatformStart}
                  >
                    {fanvuePlatform.busy ? 'Opening OAuth...' : 'Connect with Fanvue app'}
                  </button>
                  {fanvuePlatform.authUrl ? <a className="v2-text-link" href={fanvuePlatform.authUrl} target="_blank" rel="noreferrer">Open OAuth link</a> : null}
                </div>
                <div className="v2-feedback-stack" aria-live="polite">
                  {fanvuePlatform.message ? <div className="v2-success">{fanvuePlatform.message}</div> : null}
                  {fanvuePlatform.error ? <div className="v2-error">{fanvuePlatform.error}</div> : null}
                  {!fanvuePlatform.settingsBusy && !fanvuePlatformConfigured ? (
                    <div className="v2-error">Configure FANVUE_CLIENT_ID, FANVUE_CLIENT_SECRET and FANVUE_REDIRECT_URI on the server before using this option.</div>
                  ) : null}
                </div>
              </div>
            )}

            {activeProvider === 'fanvue' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-config-card v2-form-grid" onSubmit={handleFanvueSettingsSave}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Fanvue OAuth settings</p>
                      <h3>Selected model credentials</h3>
                    </div>
                    <StatusPill tone={fanvueSettings.configured ? 'good' : 'warn'}>
                      {fanvueSettings.configured ? 'ready' : 'missing'}
                    </StatusPill>
                  </div>
                  <label className="v2-field">
                    <span>Client ID</span>
                    <input
                      value={fanvueSettings.clientId}
                      onChange={(event) => updateFanvueSettings({ clientId: event.target.value })}
                      placeholder="Fanvue OAuth client id"
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Client Secret</span>
                    <input
                      type="password"
                      value={fanvueSettings.clientSecret}
                      onChange={(event) => updateFanvueSettings({ clientSecret: event.target.value })}
                      placeholder={fanvueSettings.clientSecretSet ? 'Secret is saved; paste a new one to replace it' : 'Fanvue OAuth client secret'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Redirect URI</span>
                    <input
                      value={fanvueSettings.redirectUri}
                      onChange={(event) => updateFanvueSettings({ redirectUri: event.target.value })}
                      placeholder={defaultFanvueRedirectUri()}
                      autoComplete="off"
                    />
                  </label>
                  <details className="v2-advanced-disclosure v2-field-wide">
                    <summary>Advanced Fanvue OAuth fields</summary>
                    <div className="v2-form-grid">
                      <label className="v2-field">
                        <span>Auth base URL</span>
                        <input value={fanvueSettings.authBaseUrl} onChange={(event) => updateFanvueSettings({ authBaseUrl: event.target.value })} placeholder="https://auth.fanvue.com/oauth2" />
                      </label>
                      <label className="v2-field">
                        <span>API base URL</span>
                        <input value={fanvueSettings.apiBaseUrl} onChange={(event) => updateFanvueSettings({ apiBaseUrl: event.target.value })} placeholder="https://api.fanvue.com" />
                      </label>
                      <label className="v2-field">
                        <span>API version</span>
                        <input value={fanvueSettings.apiVersion} onChange={(event) => updateFanvueSettings({ apiVersion: event.target.value })} placeholder="2025-06-26" />
                      </label>
                      <label className="v2-field">
                        <span>OAuth scopes</span>
                        <input value={fanvueSettings.oauthScopes} onChange={(event) => updateFanvueSettings({ oauthScopes: event.target.value })} placeholder="openid offline_access read:self read:chat write:chat" />
                      </label>
                      <label className="v2-field v2-field-wide">
                        <span>Webhook secret</span>
                        <input
                          type="password"
                          value={fanvueSettings.webhookSecret}
                          onChange={(event) => updateFanvueSettings({ webhookSecret: event.target.value })}
                          placeholder={fanvueSettings.webhookSecretSet ? 'Webhook secret is saved; paste a new one to replace it' : 'Optional Fanvue webhook secret'}
                          autoComplete="new-password"
                        />
                      </label>
                    </div>
                  </details>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fanvueSettings.saving || fanvueSettings.busy}>
                      {fanvueSettings.saving ? 'Saving...' : 'Save Fanvue settings'}
                    </button>
                    <span>{fanvueSettings.clientSecretSet ? 'Client secret saved' : 'Client secret not saved'}</span>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Webhook URL</span>
                    <strong>{fanvueSettings.webhookUrl || 'Set WEBHOOK_BASE_URL on the server to generate webhook URL'}</strong>
                  </div>
                  <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                    {fanvueSettings.message ? <div className="v2-success">{fanvueSettings.message}</div> : null}
                    {fanvueSettings.error ? <div className="v2-error">{fanvueSettings.error}</div> : null}
                    {!fanvueSettings.busy && !fanvueSettings.configured ? <div className="v2-error">Save Fanvue OAuth settings for this model before starting OAuth.</div> : null}
                  </div>
                </form>
                <div className="v2-action-row">
                  <button type="button" className="v2-button" disabled={!selectedModelId || fanvue.busy || fanvueSettings.busy || !fanvueSettings.configured} onClick={handleFanvueStart}>
                    {fanvue.busy ? 'Opening OAuth...' : 'Start Fanvue OAuth'}
                  </button>
                  {fanvue.authUrl ? <a className="v2-text-link" href={fanvue.authUrl} target="_blank" rel="noreferrer">Open OAuth link</a> : null}
                </div>
                <div className="v2-feedback-stack" aria-live="polite">
                  {fanvue.message ? <div className="v2-success">{fanvue.message}</div> : null}
                  {fanvue.error ? <div className="v2-error">{fanvue.error}</div> : null}
                </div>
                <form className="v2-form-grid" onSubmit={handleFanvueComplete}>
                  <label className="v2-field">
                    <span>OAuth state</span>
                    <input value={fanvue.state} onChange={(event) => updateFanvue({ state: event.target.value })} placeholder="Generated automatically" />
                  </label>
                  <label className="v2-field">
                    <span>Authorization code</span>
                    <input value={fanvue.code} onChange={(event) => updateFanvue({ code: event.target.value })} placeholder="Fallback code from redirect" />
                  </label>
                  <label className="v2-field">
                    <span>Creator UUID override</span>
                    <input value={fanvue.creatorUserUuid} onChange={(event) => updateFanvue({ creatorUserUuid: event.target.value })} placeholder="Optional if account has one creator" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || !fanvue.code.trim() || fanvue.busy}>Complete manually</button>
                </form>
                <details className="v2-advanced-disclosure">
                  <summary>Fanvue sync tools</summary>
                  <form className="v2-form-grid" onSubmit={handleFanvueSync}>
                    <label className="v2-field">
                      <span>Chat UUIDs</span>
                      <input value={fanvueSync.chatUuids} onChange={(event) => updateFanvueSync({ chatUuids: event.target.value })} placeholder="Optional, comma separated" />
                    </label>
                    <label className="v2-field">
                      <span>Since message UUID</span>
                      <input value={fanvueSync.sinceMessageUuid} onChange={(event) => updateFanvueSync({ sinceMessageUuid: event.target.value })} placeholder="Optional cursor" />
                    </label>
                    <label className="v2-field">
                      <span>Limit per chat</span>
                      <input type="number" min="1" max="50" value={fanvueSync.limit} onChange={(event) => updateFanvueSync({ limit: event.target.value })} />
                    </label>
                    <label className="v2-field">
                      <span>Max chats</span>
                      <input type="number" min="1" max="50" value={fanvueSync.maxChats} onChange={(event) => updateFanvueSync({ maxChats: event.target.value })} />
                    </label>
                    <label className="v2-toggle-row">
                      <input type="checkbox" checked={fanvueSync.enqueueIncoming} onChange={(event) => updateFanvueSync({ enqueueIncoming: event.target.checked })} />
                      <span>Queue imported inbound</span>
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fanvueSync.busy}>
                      {fanvueSync.busy ? 'Syncing...' : 'Sync Fanvue messages'}
                    </button>
                  </form>
                </details>
                {fanvueSync.message ? <div className="v2-success">{fanvueSync.message}</div> : null}
                {fanvueSync.error ? <div className="v2-error">{fanvueSync.error}</div> : null}
              </div>
            )}

            {activeProvider === 'instagram' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-config-card v2-form-grid" onSubmit={handleInstagramSettingsSave}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Instagram OAuth settings</p>
                      <h3>Selected model credentials</h3>
                    </div>
                    <StatusPill tone={instagramSettings.configured ? 'good' : 'warn'}>
                      {instagramSettings.configured ? 'ready' : 'missing'}
                    </StatusPill>
                  </div>
                  <label className="v2-field v2-field-wide">
                    <span>Callback URL for Meta</span>
                    <input value={instagramSettings.callbackUrl || defaultInstagramRedirectUri()} readOnly />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Webhook callback URL</span>
                    <input value={instagramSettings.webhookUrl || 'Save settings to generate webhook URL'} readOnly />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Webhook verify token</span>
                    <input value={instagramSettings.webhookVerifyToken || 'Save settings to generate verify token'} readOnly />
                  </label>
                  <label className="v2-field">
                    <span>Instagram OAuth App ID</span>
                    <input
                      value={instagramSettings.clientId}
                      onChange={(event) => updateInstagramSettings({ clientId: event.target.value })}
                      placeholder="Instagram API setup app id, e.g. 243..."
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Instagram OAuth App Secret</span>
                    <input
                      type="password"
                      value={instagramSettings.clientSecret}
                      onChange={(event) => updateInstagramSettings({ clientSecret: event.target.value })}
                      placeholder={instagramSettings.clientSecretSet ? 'Instagram secret saved; paste a new one to replace it' : 'Instagram API setup app secret'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Meta container App ID</span>
                    <input
                      value={instagramSettings.metaAppId}
                      onChange={(event) => updateInstagramSettings({ metaAppId: event.target.value })}
                      placeholder="Meta dashboard app id, e.g. 175..."
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Meta container App Secret</span>
                    <input
                      type="password"
                      value={instagramSettings.metaAppSecret}
                      onChange={(event) => updateInstagramSettings({ metaAppSecret: event.target.value })}
                      placeholder={instagramSettings.metaAppSecretSet ? 'Meta secret saved; paste a new one to replace it' : 'Meta App settings secret'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Redirect URI</span>
                    <input
                      value={instagramSettings.redirectUri}
                      onChange={(event) => updateInstagramSettings({ redirectUri: event.target.value })}
                      placeholder={instagramSettings.callbackUrl || defaultInstagramRedirectUri()}
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>OAuth scopes</span>
                    <input
                      value={instagramSettings.oauthScopes}
                      onChange={(event) => updateInstagramSettings({ oauthScopes: event.target.value })}
                      placeholder="instagram_business_basic,instagram_business_manage_messages"
                      autoComplete="off"
                    />
                  </label>
                  <details className="v2-advanced-disclosure v2-field-wide">
                    <summary>Advanced Instagram OAuth fields</summary>
                    <div className="v2-form-grid">
                      <label className="v2-field">
                        <span>Auth base URL</span>
                        <input value={instagramSettings.authBaseUrl} onChange={(event) => updateInstagramSettings({ authBaseUrl: event.target.value })} placeholder="https://www.instagram.com/oauth" />
                      </label>
                      <label className="v2-field">
                        <span>Token URL</span>
                        <input value={instagramSettings.tokenUrl} onChange={(event) => updateInstagramSettings({ tokenUrl: event.target.value })} placeholder="https://api.instagram.com/oauth/access_token" />
                      </label>
                      <label className="v2-field">
                        <span>Graph base URL</span>
                        <input value={instagramSettings.graphBaseUrl} onChange={(event) => updateInstagramSettings({ graphBaseUrl: event.target.value })} placeholder="https://graph.instagram.com" />
                      </label>
                    </div>
                  </details>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || instagramSettings.saving || instagramSettings.busy}>
                      {instagramSettings.saving ? 'Saving...' : 'Save Instagram settings'}
                    </button>
                    <span>
                      {instagramSettings.clientSecretSet ? 'Instagram secret saved' : 'Instagram secret not saved'}
                      {' · '}
                      {instagramSettings.metaAppSecretSet ? 'Meta secret saved' : 'Meta secret optional'}
                    </span>
                  </div>
                  <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                    {instagramSettings.message ? <div className="v2-success">{instagramSettings.message}</div> : null}
                    {instagramSettings.error ? <div className="v2-error">{instagramSettings.error}</div> : null}
                    {!instagramSettings.busy && !instagramSettings.configured ? <div className="v2-error">Save Instagram App ID, App Secret and Redirect URI before starting OAuth.</div> : null}
                  </div>
                </form>
                <div className="v2-action-row">
                  <button type="button" className="v2-button" disabled={!selectedModelId || instagram.busy || instagramSettings.busy || !instagramSettings.configured} onClick={handleInstagramStart}>
                    {instagram.busy ? 'Opening OAuth...' : 'Start Instagram OAuth'}
                  </button>
                  {instagram.authUrl ? <a className="v2-text-link" href={instagram.authUrl} target="_blank" rel="noreferrer">Open OAuth link</a> : null}
                </div>
                <div className="v2-feedback-stack" aria-live="polite">
                  {instagram.message ? <div className="v2-success">{instagram.message}</div> : null}
                  {instagram.error ? <div className="v2-error">{instagram.error}</div> : null}
                </div>
              </div>
            )}

            {activeProvider === 'telegram_account' && (
              <div className="v2-connection-panel-stack">
                <details open={!telegramSettings.configured} className="v2-field-wide">
                  <summary>Telegram API app settings</summary>
                  <form className="v2-form-grid" onSubmit={handleTelegramSettingsSave}>
                    <label className="v2-field">
                      <span>API ID</span>
                      <input value={telegramSettings.apiId} onChange={(event) => updateTelegramSettings({ apiId: event.target.value })} placeholder="api_id from my.telegram.org" />
                    </label>
                    <label className="v2-field">
                      <span>API Hash</span>
                      <input type="password" value={telegramSettings.apiHash} onChange={(event) => updateTelegramSettings({ apiHash: event.target.value })} placeholder={telegramSettings.apiHashSet ? 'Saved - leave blank to keep' : 'api_hash from my.telegram.org'} />
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || telegramSettings.saving || !telegramSettings.apiId.trim()}>
                      {telegramSettings.saving ? 'Saving...' : 'Save Telegram API settings'}
                    </button>
                    <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                      {telegramSettings.configured ? <div className="v2-success">Telegram API settings are configured{telegramSettings.source ? ` (${telegramSettings.source})` : ''}.</div> : null}
                      {!telegramSettings.busy && !telegramSettings.configured ? <div className="v2-error">Save Telegram API ID and API Hash before sending a login code.</div> : null}
                      {telegramSettings.message ? <div className="v2-success">{telegramSettings.message}</div> : null}
                      {telegramSettings.error ? <div className="v2-error">{telegramSettings.error}</div> : null}
                    </div>
                  </form>
                </details>
                <div className="v2-telegram-login-flow">
                  <form className="v2-telegram-step" onSubmit={handleTelegramStart}>
                    <div className="v2-step-number">1</div>
                    <div className="v2-telegram-step-body">
                      <label className="v2-field">
                        <span>Phone number</span>
                        <input
                          value={telegram.phone}
                          onChange={(event) => updateTelegram({ phone: event.target.value, error: '' })}
                          placeholder="+15551234567"
                          autoComplete="tel"
                        />
                      </label>
                      <button type="submit" className="v2-button" disabled={!selectedModelId || !telegram.phone.trim() || telegram.busy || !telegramSettings.configured}>
                        {telegram.busy ? 'Sending...' : 'Send Telegram code'}
                      </button>
                    </div>
                  </form>
                  <form className="v2-telegram-step" onSubmit={handleTelegramComplete}>
                    <div className="v2-step-number">2</div>
                    <div className="v2-telegram-step-body">
                      <div className="v2-telegram-code-grid">
                        <label className="v2-field">
                          <span>Login code</span>
                          <input
                            value={telegram.code}
                            onChange={handleTelegramCodeChange}
                            placeholder="5-digit code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                          />
                        </label>
                        <label className="v2-field">
                          <span>Two-step password</span>
                          <input
                            type="password"
                            value={telegram.password}
                            onChange={(event) => updateTelegram({ password: event.target.value, error: '' })}
                            placeholder={telegram.passwordRequired ? 'Required' : 'Only if Telegram asks'}
                            autoComplete="current-password"
                          />
                        </label>
                      </div>
                      <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || !telegram.phone.trim() || (!telegram.code.trim() && !(telegram.passwordRequired && telegram.password)) || telegram.busy}>Complete Telegram login</button>
                    </div>
                  </form>
                </div>
                <div className="v2-feedback-stack" aria-live="polite">
                  {telegramReauthRequired(selectedProviderLink) ? (
                    <div className="v2-error"><strong>Re-auth required.</strong> This Telegram session expired or was revoked. Send a new login code and complete account login again.</div>
                  ) : null}
                  {telegram.message ? <div className="v2-success">{telegram.message}</div> : null}
                  {telegram.error ? <div className="v2-error">{telegram.error}</div> : null}
                  {!telegram.codeSent && selectedProviderStatus === 'connecting' ? (
                    <div className="v2-info">A previous Telegram code may have expired. Press Send Telegram code again, then enter the newest code from Telegram.</div>
                  ) : null}
                </div>
              </div>
            )}

            {activeProvider === 'telegram_tribute' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-config-card v2-form-grid" onSubmit={handleTributeSettingsSave}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Telegram Tribute</p>
                      <h3>Private-chat webhook</h3>
                    </div>
                    <StatusPill tone={tributeSettings.connected ? 'good' : 'warn'}>
                      {tributeSettings.connected ? 'connected' : 'needs key'}
                    </StatusPill>
                  </div>
                  <label className="v2-field">
                    <span>Creator label</span>
                    <input
                      value={tributeSettings.externalId}
                      onChange={(event) => updateTributeSettings({ externalId: event.target.value })}
                      placeholder="vesna or Tribute account label"
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field">
                    <span>API key</span>
                    <input
                      type="password"
                      value={tributeSettings.apiKey}
                      onChange={(event) => updateTributeSettings({ apiKey: event.target.value, error: '' })}
                      placeholder={tributeSettings.apiKeySet ? 'Key saved; paste a new one to replace it' : 'Tribute API key'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Webhook URL</span>
                    <input value={tributeSettings.webhookUrl || 'Set WEBHOOK_BASE_URL on the server to generate webhook URL'} readOnly />
                  </label>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || tributeSettings.saving || tributeSettings.busy}>
                      {tributeSettings.saving ? 'Saving...' : 'Save channel'}
                    </button>
                    <button
                      type="button"
                      className="v2-button"
                      disabled={!selectedModelId || tributeSettings.saving || tributeSettings.busy || (!tributeSettings.apiKey.trim() && !tributeSettings.apiKeySet)}
                      onClick={() => saveTributeSettings({ verifyNow: true })}
                    >
                      Save and verify
                    </button>
                    <span>{tributeSettings.apiKeySet ? 'Key saved' : 'Key not saved'}</span>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Products probe</span>
                    <strong>{tributeSettings.productsProbeCount == null ? 'Not checked' : `${tributeSettings.productsProbeCount} item probe`}</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Last check</span>
                    <strong>{tributeSettings.lastHealthCheckAt || 'No health check yet'}</strong>
                  </div>
                  <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                    {tributeSettings.message ? <div className="v2-success">{tributeSettings.message}</div> : null}
                    {tributeSettings.error ? <div className="v2-error">{tributeSettings.error}</div> : null}
                    {!tributeSettings.busy && !tributeSettings.configured ? <div className="v2-error">Save a Tribute API key, then copy the webhook URL into Tribute API Keys settings.</div> : null}
                  </div>
                </form>
              </div>
            )}

            {activeProvider === 'telegram_bot_payments' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-config-card v2-form-grid" onSubmit={handleTelegramPaymentsSettingsSave}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Telegram Stars</p>
                      <h3>Native checkout bot</h3>
                    </div>
                    <StatusPill tone={telegramPayments.connected ? 'good' : 'warn'}>
                      {telegramPayments.connected ? 'connected' : 'needs bot token'}
                    </StatusPill>
                  </div>
                  <label className="v2-field">
                    <span>Bot token</span>
                    <input
                      type="password"
                      value={telegramPayments.botToken}
                      onChange={(event) => updateTelegramPayments({ botToken: event.target.value, error: '' })}
                      placeholder={telegramPayments.botTokenSet ? 'Token saved; paste a new one to replace it' : 'BotFather token'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Business connection ID</span>
                    <input
                      value={telegramPayments.businessConnectionId}
                      onChange={(event) => updateTelegramPayments({ businessConnectionId: event.target.value })}
                      placeholder="Optional for Business paid media"
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Webhook URL</span>
                    <input value={telegramPayments.webhookUrl || 'Set WEBHOOK_BASE_URL on the server to generate webhook URL'} readOnly />
                  </label>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || telegramPayments.saving || telegramPayments.busy}>
                      {telegramPayments.saving ? 'Saving...' : 'Save bot'}
                    </button>
                    <button
                      type="button"
                      className="v2-button"
                      disabled={!selectedModelId || telegramPayments.saving || telegramPayments.busy || (!telegramPayments.botToken.trim() && !telegramPayments.botTokenSet)}
                      onClick={() => saveTelegramPaymentsSettings({ setWebhook: true })}
                    >
                      Save and set webhook
                    </button>
                    <span>{telegramPayments.botTokenSet ? 'Token saved' : 'Token not saved'}</span>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Bot</span>
                    <strong>{telegramPayments.botUsername ? `@${telegramPayments.botUsername}` : telegramPayments.botId || 'Not checked'}</strong>
                  </div>
                  <div className="v2-quick-summary v2-field-wide">
                    <span>Last check</span>
                    <strong>{telegramPayments.lastHealthCheckAt || 'No health check yet'}</strong>
                  </div>
                  <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                    {telegramPayments.message ? <div className="v2-success">{telegramPayments.message}</div> : null}
                    {telegramPayments.error ? <div className="v2-error">{telegramPayments.error}</div> : null}
                    {!telegramPayments.busy && !telegramPayments.configured ? <div className="v2-error">Save a Telegram bot token, then set the webhook before accepting Stars payments.</div> : null}
                  </div>
                </form>

                <form className="v2-config-card v2-form-grid" onSubmit={handleTelegramInvoiceLink}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">Invoice link</p>
                      <h3>Create Stars checkout</h3>
                    </div>
                    <StatusPill tone={telegramPayments.connected ? 'good' : 'warn'}>
                      {telegramPayments.connected ? 'ready' : 'configure bot first'}
                    </StatusPill>
                  </div>
                  <label className="v2-field">
                    <span>Title</span>
                    <input
                      value={telegramPayments.invoiceTitle}
                      onChange={(event) => updateTelegramPayments({ invoiceTitle: event.target.value })}
                      maxLength={32}
                    />
                  </label>
                  <label className="v2-field">
                    <span>Stars</span>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      value={telegramPayments.invoiceStars}
                      onChange={(event) => updateTelegramPayments({ invoiceStars: event.target.value })}
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Description</span>
                    <input
                      value={telegramPayments.invoiceDescription}
                      onChange={(event) => updateTelegramPayments({ invoiceDescription: event.target.value })}
                      maxLength={255}
                    />
                  </label>
                  <details className="v2-advanced-disclosure v2-field-wide">
                    <summary>Advanced invoice fields</summary>
                    <div className="v2-form-grid">
                      <label className="v2-field">
                        <span>Payload</span>
                        <input
                          value={telegramPayments.invoicePayload}
                          onChange={(event) => updateTelegramPayments({ invoicePayload: event.target.value })}
                          placeholder="Generated automatically"
                          maxLength={128}
                        />
                      </label>
                      <label className="v2-field">
                        <span>Photo URL</span>
                        <input
                          value={telegramPayments.invoicePhotoUrl}
                          onChange={(event) => updateTelegramPayments({ invoicePhotoUrl: event.target.value })}
                          placeholder="Optional cover image URL"
                        />
                      </label>
                    </div>
                  </details>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || telegramPayments.saving || !telegramPayments.configured}>
                      {telegramPayments.saving ? 'Creating...' : 'Create invoice link'}
                    </button>
                  </div>
                  {telegramPayments.invoiceLink ? (
                    <label className="v2-field v2-field-wide">
                      <span>Invoice link</span>
                      <input value={telegramPayments.invoiceLink} readOnly />
                    </label>
                  ) : null}
                </form>
              </div>
            )}

            {activeProvider === 'aigirlfactory' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-config-card v2-form-grid" onSubmit={handleAigfSettingsSave}>
                  <div className="v2-card-heading v2-field-wide">
                    <div>
                      <p className="v2-eyebrow">AIGirlFactory remote engine</p>
                      <h3>Website chat channel</h3>
                    </div>
                    <StatusPill tone={aigfSettings.connected ? 'good' : 'warn'}>
                      {aigfSettings.connected ? 'connected' : 'needs token'}
                    </StatusPill>
                  </div>
                  <label className="v2-field">
                    <span>Persona key</span>
                    <input
                      value={aigfSettings.personaKey}
                      onChange={(event) => updateAigfSettings({ personaKey: event.target.value })}
                      placeholder="vesna"
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field">
                    <span>Channel</span>
                    <input
                      value={aigfSettings.channel}
                      onChange={(event) => updateAigfSettings({ channel: event.target.value })}
                      placeholder="aigirlfriend_site"
                      autoComplete="off"
                    />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Endpoint URL</span>
                    <input value={aigfSettings.endpointUrl} readOnly />
                  </label>
                  <label className="v2-field v2-field-wide">
                    <span>Shared token</span>
                    <input
                      type="password"
                      value={aigfSettings.apiToken}
                      onChange={(event) => updateAigfSettings({ apiToken: event.target.value })}
                      placeholder={aigfSettings.apiTokenSet ? 'Token saved; paste a new one to replace it' : 'Paste token or generate one'}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="v2-toggle-row v2-field-wide">
                    <input
                      type="checkbox"
                      checked={aigfSettings.requireSignature}
                      onChange={(event) => updateAigfSettings({ requireSignature: event.target.checked })}
                    />
                    <span>Require X-AIGF-Signature HMAC</span>
                  </label>
                  <div className="v2-action-row v2-field-wide">
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || aigfSettings.saving || aigfSettings.busy}>
                      {aigfSettings.saving ? 'Saving...' : 'Save channel'}
                    </button>
                    <button
                      type="button"
                      className="v2-button"
                      disabled={!selectedModelId || aigfSettings.saving || aigfSettings.busy}
                      onClick={() => saveAigfSettings({ generateToken: true })}
                    >
                      Generate token
                    </button>
                    <span>{aigfSettings.apiTokenSet ? 'Token saved' : 'Token not saved'}</span>
                  </div>
                  {aigfSettings.apiTokenOnce ? (
                    <div className="v2-quick-summary v2-field-wide">
                      <span>Generated token</span>
                      <strong>{aigfSettings.apiTokenOnce}</strong>
                    </div>
                  ) : null}
                  <div className="v2-feedback-stack v2-field-wide" aria-live="polite">
                    {aigfSettings.message ? <div className="v2-success">{aigfSettings.message}</div> : null}
                    {aigfSettings.error ? <div className="v2-error">{aigfSettings.error}</div> : null}
                    {!aigfSettings.busy && !aigfSettings.configured ? <div className="v2-error">Generate or paste a shared token, then copy the endpoint URL and token into AIGirlFactory.</div> : null}
                  </div>
                </form>
              </div>
            )}

            {activeProvider === 'onlyfansapi' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-form-grid" onSubmit={handleOnlyFansApiConnect}>
                  <label className="v2-field">
                    <span>Account ID</span>
                    <input value={onlyfansapi.accountId} onChange={(event) => updateOnlyfansapi({ accountId: event.target.value })} placeholder="Optional: auto-detect from model" />
                  </label>
                  <label className="v2-field">
                    <span>API key</span>
                    <input type="password" value={onlyfansapi.apiKey} onChange={(event) => updateOnlyfansapi({ apiKey: event.target.value })} placeholder="Optional if global key is configured" />
                  </label>
                  <label className="v2-field">
                    <span>Connection ID</span>
                    <input value={onlyfansapi.connectionId} onChange={(event) => updateOnlyfansapi({ connectionId: event.target.value })} placeholder="Optional override" />
                  </label>
                  <label className="v2-field">
                    <span>Email label</span>
                    <input value={onlyfansapi.email} onChange={(event) => updateOnlyfansapi({ email: event.target.value })} placeholder="Optional operator label" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || (!onlyfansapi.accountId.trim() && !onlyfansapi.apiKey.trim()) || onlyfansapi.busy}>
                    {onlyfansapi.busy ? 'Connecting...' : 'Connect OnlyFansAPI account'}
                  </button>
                </form>
                {onlyfansapi.message ? <div className="v2-success">{onlyfansapi.message}</div> : null}
                {onlyfansapi.error ? <div className="v2-error">{onlyfansapi.error}</div> : null}
              </div>
            )}

            {activeProvider === 'onlymonster' && (
              <div className="v2-connection-panel-stack">
                <form className="v2-form-grid" onSubmit={handleOnlyMonsterConnect}>
                  <label className="v2-field">
                    <span>Account ID</span>
                    <input value={onlymonster.accountId} onChange={(event) => updateOnlymonster({ accountId: event.target.value })} placeholder="Optional: auto-detect from token" />
                  </label>
                  <label className="v2-field">
                    <span>API token</span>
                    <input type="password" value={onlymonster.apiToken} onChange={(event) => updateOnlymonster({ apiToken: event.target.value })} placeholder="Optional if global token is configured" />
                  </label>
                  <label className="v2-field">
                    <span>Platform account ID</span>
                    <input value={onlymonster.platformAccountId} onChange={(event) => updateOnlymonster({ platformAccountId: event.target.value })} placeholder="Optional OnlyFans account id" />
                  </label>
                  <label className="v2-field">
                    <span>Username</span>
                    <input value={onlymonster.username} onChange={(event) => updateOnlymonster({ username: event.target.value })} placeholder="Optional lookup hint" />
                  </label>
                  <label className="v2-field">
                    <span>Email label</span>
                    <input value={onlymonster.email} onChange={(event) => updateOnlymonster({ email: event.target.value })} placeholder="Optional lookup hint" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || onlymonster.busy}>
                    {onlymonster.busy ? 'Connecting...' : 'Connect OnlyMonster account'}
                  </button>
                </form>
                {onlymonster.message ? <div className="v2-success">{onlymonster.message}</div> : null}
                {onlymonster.error ? <div className="v2-error">{onlymonster.error}</div> : null}
              </div>
            )}

            {activeProvider === 'fansly' && (
              <div className="v2-connection-panel-stack">
                <div className="v2-info">Authenticate Fansly here with the OnlyFansAPI key. Email and authenticator-app 2FA challenges are supported. After authentication FanLTV creates the provider link and webhook automatically.</div>
                <p className="v2-eyebrow">1. Check OnlyFansAPI key</p>
                <form className="v2-form-grid" onSubmit={handleFanslyAccountsLoad}>
                  <label className="v2-field">
                    <span>OnlyFansAPI key</span>
                    <input type="password" value={fansly.apiKey} onChange={(event) => updateFansly({ apiKey: event.target.value, error: '' })} placeholder="ofapi_… or leave blank to use the server key" autoComplete="off" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fansly.authBusy}>
                    {fansly.authBusy ? 'Checking…' : 'Check key and load accounts'}
                  </button>
                </form>
                {fansly.accounts.length ? (
                  <label className="v2-field">
                    <span>Fansly account</span>
                    <select
                      value={fansly.accountId}
                      onChange={(event) => {
                        const account = fansly.accounts.find((item) => item.id === event.target.value) || null
                        updateFansly({ accountId: event.target.value, authStatus: account, error: '', message: '' })
                      }}
                    >
                      <option value="">Select account</option>
                      {fansly.accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.display_name || account.fansly_username || account.id} · {account.is_authenticated ? 'authenticated' : (account.status || 'authentication required')}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {fansly.authStatus ? (
                  <div className="v2-info">
                    <StatusPill tone={fansly.authStatus.is_authenticated ? 'good' : (fansly.authStatus.needs_code ? 'warn' : String(fansly.authStatus.status || '').includes('fail') ? 'bad' : 'neutral')}>
                      {fansly.authStatus.is_authenticated ? 'authenticated' : (fansly.authStatus.status || 'pending')}
                    </StatusPill>
                    {' '}{fansly.authStatus.display_name || fansly.authStatus.fansly_username || fansly.accountId}
                  </div>
                ) : null}
                {fansly.accountId && !fansly.authStatus?.is_authenticated ? (
                  <div className="v2-form-grid">
                    <button type="button" className="v2-button v2-button-secondary" disabled={fansly.authBusy} onClick={handleFanslyReauthenticate}>
                      Re-authenticate saved account
                    </button>
                    <button type="button" className="v2-button v2-button-ghost" disabled={fansly.authBusy} onClick={() => handleFanslyAuthStatus()}>
                      Check authentication status
                    </button>
                  </div>
                ) : null}

                <p className="v2-eyebrow">2. Authenticate Fansly account</p>
                <form className="v2-form-grid" onSubmit={handleFanslyAuthStart}>
                  <label className="v2-field">
                    <span>Fansly username or email</span>
                    <input value={fansly.username} onChange={(event) => updateFansly({ username: event.target.value, error: '' })} placeholder="creator or creator@example.com" autoComplete="username" />
                  </label>
                  <label className="v2-field">
                    <span>Fansly password</span>
                    <input type="password" value={fansly.password} onChange={(event) => updateFansly({ password: event.target.value, error: '' })} autoComplete="current-password" />
                  </label>
                  <label className="v2-field">
                    <span>Managed proxy</span>
                    <select value={fansly.proxyCountry} onChange={(event) => updateFansly({ proxyCountry: event.target.value })}>
                      <option value="">OnlyFansAPI default</option>
                      <option value="us">United States</option>
                      <option value="uk">United Kingdom</option>
                    </select>
                  </label>
                  <label className="v2-field">
                    <span>Model label</span>
                    <input value={fansly.email} onChange={(event) => updateFansly({ email: event.target.value })} placeholder="Optional FanLTV label" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || !fansly.username.trim() || !fansly.password || fansly.authBusy}>
                    {fansly.authBusy ? 'Starting…' : 'Start Fansly authentication'}
                  </button>
                </form>
                {fansly.authStatus?.needs_code ? (
                  <form className="v2-form-grid" onSubmit={handleFanslyTwoFactor}>
                    <label className="v2-field">
                      <span>{fansly.authStatus.twofa_type === 2 ? 'Email verification code' : 'Authenticator code'}</span>
                      <input value={fansly.code} onChange={(event) => updateFansly({ code: event.target.value.replace(/\s/g, ''), error: '' })} inputMode="numeric" autoComplete="one-time-code" />
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!fansly.code.trim() || fansly.authBusy}>
                      {fansly.authBusy ? 'Submitting…' : 'Submit Fansly 2FA'}
                    </button>
                  </form>
                ) : null}
                {fansly.message ? <div className="v2-success">{fansly.message}</div> : null}
                {fansly.error ? <div className="v2-error">{fansly.error}</div> : null}
                <details className="v2-advanced-disclosure">
                  <summary>Connect an already authenticated account</summary>
                  <form className="v2-form-grid" onSubmit={handleFanslyConnect}>
                    <label className="v2-field">
                      <span>Account ID</span>
                      <input value={fansly.accountId} onChange={(event) => updateFansly({ accountId: event.target.value })} placeholder="fansly_acct_..." />
                    </label>
                    <label className="v2-field">
                      <span>Numeric Fansly ID</span>
                      <input value={fansly.fanslyId} onChange={(event) => updateFansly({ fanslyId: event.target.value })} />
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || !fansly.accountId.trim() || fansly.busy}>
                      {fansly.busy ? 'Connecting…' : 'Connect authenticated account'}
                    </button>
                  </form>
                </details>
                <details className="v2-advanced-disclosure">
                  <summary>Experimental direct media upload</summary>
                  {fanslyDirect.busy ? <LoadingBlock label="Loading direct upload settings" /> : (
                    <form className="v2-form-grid" onSubmit={handleFanslyDirectSave}>
                      <div className="v2-info v2-field-wide">
                        Uses the current Fansly web session for FYP media uploads when OnlyFansAPI upload is unavailable. The session is encrypted and can be disabled without changing chat or webhook connections.
                      </div>
                      <label className="v2-field v2-field-wide">
                        <span>Fansly web session</span>
                        <input
                          type="password"
                          value={fanslyDirect.sessionToken}
                          onChange={(event) => updateFanslyDirect({ sessionToken: event.target.value, error: '' })}
                          placeholder={fanslyDirect.sessionTokenSet ? 'Session stored - leave blank to verify again' : 'Paste token or fansly_active_session JSON'}
                          autoComplete="off"
                        />
                      </label>
                      <label className="v2-toggle-row v2-field-wide">
                        <input
                          type="checkbox"
                          checked={fanslyDirect.enabled}
                          onChange={(event) => updateFanslyDirect({ enabled: event.target.checked, error: '' })}
                        />
                        <span>Use direct upload for FYP posts</span>
                      </label>
                      {fanslyDirect.verified ? (
                        <div className="v2-info v2-field-wide">
                          <StatusPill tone={fanslyDirect.enabled ? 'good' : 'neutral'}>
                            {fanslyDirect.enabled ? 'enabled' : 'verified'}
                          </StatusPill>
                          {' '}{fanslyDirect.displayName || fanslyDirect.username || fanslyDirect.accountId}
                          {fanslyDirect.lastVerifiedAt ? ` - ${formatDate(fanslyDirect.lastVerifiedAt)}` : ''}
                        </div>
                      ) : null}
                      <div className="v2-action-row v2-field-wide">
                        <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fanslyDirect.saving || (!fanslyDirect.sessionToken.trim() && !fanslyDirect.sessionTokenSet)}>
                          {fanslyDirect.saving ? 'Verifying...' : 'Verify and save'}
                        </button>
                        {fanslyDirect.sessionTokenSet ? (
                          <button type="button" className="v2-button v2-button-ghost" disabled={fanslyDirect.saving} onClick={handleFanslyDirectClear}>
                            Remove session
                          </button>
                        ) : null}
                      </div>
                    </form>
                  )}
                  {fanslyDirect.message ? <div className="v2-success">{fanslyDirect.message}</div> : null}
                  {fanslyDirect.error ? <div className="v2-error">{fanslyDirect.error}</div> : null}
                </details>
                <details className="v2-advanced-disclosure">
                  <summary>Fansly sync tools</summary>
                  <div className="v2-info">Text, media bundles, paid unlocks, free previews, subscription gates and follow gates are supported. Local media is uploaded to the selected Fansly account on first send and reused afterward. Webhooks deliver live events when enabled by the OnlyFansAPI plan; polling provides catch-up.</div>
                  <form className="v2-form-grid" onSubmit={handleFanslySync}>
                    <label className="v2-field">
                      <span>Limit per chat</span>
                      <input type="number" min="1" max="100" value={fanslySync.limit} onChange={(event) => updateFanslySync({ limit: event.target.value })} />
                    </label>
                    <label className="v2-field">
                      <span>Max chats</span>
                      <input type="number" min="1" max="500" value={fanslySync.maxChats} onChange={(event) => updateFanslySync({ maxChats: event.target.value })} />
                    </label>
                    <label className="v2-toggle-row">
                      <input type="checkbox" checked={fanslySync.enqueueUnanswered} onChange={(event) => updateFanslySync({ enqueueUnanswered: event.target.checked })} />
                      <span>Queue unanswered inbound</span>
                    </label>
                    <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fanslySync.busy}>
                      {fanslySync.busy ? 'Syncing...' : 'Sync Fansly messages'}
                    </button>
                  </form>
                </details>
                {fanslySync.message ? <div className="v2-success">{fanslySync.message}</div> : null}
                {fanslySync.error ? <div className="v2-error">{fanslySync.error}</div> : null}
              </div>
            )}

            {activeProvider === 'fanltv_ofapi' && (
              <div className="v2-connection-panel-stack">
                <div className="v2-error">Backup only: requires a connected OnlyFansAPI or OnlyMonster link. Browser sessions may expire and paid media is not sent through this fallback.</div>
                <form className="v2-form-grid" onSubmit={handleFanltvOfapiConnect}>
                  <label className="v2-field">
                    <span>Account ID</span>
                    <input value={fanltvOfapi.accountId} onChange={(event) => updateFanltvOfapi({ accountId: event.target.value })} placeholder="OnlyFans account/session id" />
                  </label>
                  <label className="v2-field">
                    <span>API key</span>
                    <input type="password" value={fanltvOfapi.apiKey} onChange={(event) => updateFanltvOfapi({ apiKey: event.target.value })} placeholder="Optional if compat key is configured" />
                  </label>
                  <label className="v2-field">
                    <span>Connection ID</span>
                    <input value={fanltvOfapi.connectionId} onChange={(event) => updateFanltvOfapi({ connectionId: event.target.value })} placeholder="Optional override" />
                  </label>
                  <label className="v2-field">
                    <span>Email label</span>
                    <input value={fanltvOfapi.email} onChange={(event) => updateFanltvOfapi({ email: event.target.value })} placeholder="Optional operator label" />
                  </label>
                  <button type="submit" className="v2-button v2-button-secondary" disabled={!selectedModelId || fanltvOfapi.busy}>
                    {fanltvOfapi.busy ? 'Connecting...' : 'Connect backup'}
                  </button>
                </form>
                {fanltvOfapi.message ? <div className="v2-success">{fanltvOfapi.message}</div> : null}
                {fanltvOfapi.error ? <div className="v2-error">{fanltvOfapi.error}</div> : null}
              </div>
            )}

            {selectedProvider.state === 'planned' ? (
              <EmptyState title={`${selectedProvider.name} is not live yet`} description="Production setup is limited to active providers until this connector is implemented." />
            ) : null}
          </article>
        </div>
      </Section>

      <div className="v2-metrics-grid">
        <MetricCard label="Provider accounts" value={rows.length} detail="Linked to models" />
        <MetricCard label="Connected" value={connected.length} detail="Ready for operations" tone="good" />
        <MetricCard label="Need attention" value={failed.length} detail="Failed or blocked" tone={failed.length ? 'bad' : 'good'} />
        <MetricCard label="Active providers" value={activeProviders.length} detail="Ready to connect now" />
      </div>

      <Section eyebrow="Accounts" title="Connected accounts by model" description="Each row is one provider account with its own status and external id.">
        {healthCheck.message ? <div className="v2-success">{healthCheck.message}</div> : null}
        {healthCheck.error ? <div className="v2-error">{healthCheck.error}</div> : null}
        {rows.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>External account</th>
                  <th>Status</th>
                  <th>Webhook</th>
                  <th>Sync</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ model, link }) => {
                  const rowKey = `${model.id}:${link.id || link.provider}`
                  const busy = healthCheck.busyKey === `${model.id}:${link.id}`
                  const webhookStatus = providerWebhookStatus(link)
                  return (
                    <tr key={rowKey}>
                      <td>{providerLabel(link.provider)}</td>
                      <td>{modelTitle(model)}</td>
                      <td>{link.external_id || link.email || link.username || 'Not provided'}</td>
                      <td><StatusPill tone={statusTone(providerStatus(link))}>{connectionStatusLabel(link, providerStatus(link))}</StatusPill></td>
                      <td>
                        {webhookStatus ? (
                          <div className="v2-provider-runtime">
                            <StatusPill tone={statusTone(webhookStatus)}>{webhookStatus}</StatusPill>
                            <span>{providerWebhookDetail(link)}</span>
                          </div>
                        ) : (
                          <span className="v2-muted">No webhook</span>
                        )}
                      </td>
                      <td>
                        {link.sync_status ? (
                          <div className="v2-provider-sync">
                            <StatusPill tone={statusTone(link.sync_status)}>{link.sync_status}</StatusPill>
                            <span>{formatSyncStats(link)}</span>
                          </div>
                        ) : (
                          <span className="v2-muted">No sync</span>
                        )}
                      </td>
                      <td>
                        <div className="v2-action-row">
                          {link.id ? (
                            <button
                              type="button"
                              className="v2-button v2-button-secondary"
                              disabled={busy}
                              onClick={() => handleHealthCheck(model.id, link.id)}
                            >
                              {busy ? 'Checking...' : 'Check health'}
                            </button>
                          ) : null}
                          <Link to={`/v2/models/${model.id}`} className="v2-text-link">Open model</Link>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No provider accounts found" description="Connect a model account to start managing platform health here." />
        )}
      </Section>
    </div>
  )
}
